import React, { useEffect, useRef, useState, useCallback, lazy, Suspense } from "react";
import { isQrChat, isMetaContactThread, isWhatsAppStyle, hasMetaServiceWindow } from "../../../utils/platformFamily";
import { isQrNumberUsableState, composerQrState, isChatNumberUnknownAmongSeveral } from "../../../utils/qrNumberState";
import {
  Box,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Divider,
  Button,
  Fade,
  Menu,
  MenuItem,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { DROPDOWN_PAPER_SX, DROPDOWN_ITEM_SX } from "../../../utils/dropdownMenuStyles";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  selectMessages,
  selectUserDetailsData,
  selectContactInfo,
  selectSelectedTemplate,
  selectTeamMembersFromInbox,
} from "../../../redux/selectors";
import { fetchQuickReplies } from "../../../redux/Slice/Settings/quickRepliesSlice";
import { setSelectedOption } from "../../../redux/Slice/Settings/settingsSlice";
import { useOrderedQuickReplies, saveQuickReplyOrder } from "../../../utils/quickReplyOrder";
import {
  sendAudioByUpload,
  sendDocumentByUpload,
  sendImageByUpload,
  sendGalleryItemMessage,
  sendMessage,
  sendVideoByUpload,
  sendCatalogProductsMessage,
  addMessage,
  removeOptimisticMessage,
} from "../../../redux/Slice/Inbox/InboxMessageSlice.js"; // Import addMessage action
import { sendTeamMemberMessage, addMessage as addTeamMemberMessage } from "../../../redux/Slice/Inbox/teamMemberChatSlice";
import { fetchContactById, invalidateCachedContact, setContactInfo } from "../../../redux/Slice/Inbox/inboxInfoSlice";
import { EmojiHappy, Paperclip2, DocumentText1, Flash, MessageFavorite, Microphone2, Clock, Send2, Monitor, Cloud, Magicpen, AddCircle, ShoppingCart, ArrowDown2, TickCircle } from "iconsax-react";
// Refine — not in our iconsax-react package (free tier has no pen+sparkle
// combo, confirmed on iconsax's own site too, 2026-08-29). This "ai-editing"
// glyph is from Hugeicons' free stroke set instead — same real .svg-asset +
// SVGR ReactComponent convention as ChatCard.jsx's Sent/Read/Error icons and
// ChatboxNavbar.jsx's AiUsersIcon, not hardcoded inline JSX.
import { ReactComponent as AiRefineIcon } from "../../../Images/Inbox/AiRefine.svg";
import Lottie from "lottie-react";
import aiTwinkleLoadingAnimation from "../../../assets/lottie/ai-twinkle-loading.json";
import CloseIcon from "@mui/icons-material/Close"; // Importing the Close icon
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ChatActionBox from "./ChatActionBox";
import IconButtonWithTooltip from "../Shared/IconButtonWithTooltip";
import ExpiredServiceBox from "./TemplateSendFeatures/ExpiredServiceBox.jsx";
import BlockedContactBox from "./TemplateSendFeatures/BlockedContactBox.jsx";
import AwaitingCommentReplyBox from "./TemplateSendFeatures/AwaitingCommentReplyBox.jsx";
import TemplateSend from "./TemplateSendFeatures/TemplateSend.jsx";
import { updateSelectedTemplate } from "../../../redux/Slice/Templates/templateSlice";
import TemplateSendBox from "./TemplateSendFeatures/TemplateSendBox.jsx";
import InstagramTemplateSend from "./TemplateSendFeatures/InstagramTemplateSend.jsx";
import InstagramTemplateForm from "./TemplateSendFeatures/InstagramTemplateForm.jsx";
import InboxCatalogSend from "./TemplateSendFeatures/InboxCatalogSend.jsx";

import AudioRecorder from "./ChatBoxFeatures/AudioRecorder.jsx";
import ClusterDenMediaDialog from "./ClusterDenMediaDialog.jsx";
import { getSocketId, trackPendingMessage } from "../../../utils/socketHandler"; // Import socket tracking functions
import ScheduleMessageDialog from "./ScheduleMessageDialog";
import MentionAutocomplete from "./MentionAutocomplete";
import QuickReplyAutocomplete from "./QuickReplyAutocomplete";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";
import toast from "react-hot-toast";
import useWhatsappWebStatus from "../../../hooks/useWhatsappWebStatus";
import { sendWhatsappWebMedia, WHATSAPP_WEB_MEDIA_TYPES, WHATSAPP_WEB_MAX_CAPTION_LENGTH } from "../../../services/whatsappWebApi";
import useChatQrNumber from "../../../features/whatsapp/hooks/useChatQrNumber";
import { checkBeforeOutbound } from "../../../utils/subscriptionError";
import {
  classifyInboxMediaFile,
  getInboxAcceptString,
} from "../../../utils/inboxMediaValidation";

const EmojiPicker = lazy(() =>
  Promise.all([
    import("@emoji-mart/data"),
    import("@emoji-mart/react"),
  ]).then(([dataModule, { default: Picker }]) => ({
    default: (props) => <Picker data={dataModule.default} {...props} />,
  }))
);

// Shared hover treatment for every composer toolbar icon — same rail-hover
// rgba formula used throughout the rest of the app (ChatboxNavbar, rail
// popovers, etc.), not a one-off (Rahim, 2026-08-22: "use our new icons...
// better spacing").
const composerIconBtnSx = {
  padding: "7px",
  "&:hover": {
    backgroundColor: (t) => t.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)",
  },
};

// Rotating "thinking" words shown while the AI is generating a reply — plain
// text, no icon (Rahim, 2026-08-28: "no icon only word with ... loader
// effect and words changing after a sec or two").
const AI_THINKING_WORDS = ["Pondering", "Noodling", "Percolating", "Simmering"];

// Formats a QR send-limit reset instant (burst-pause resumeAt / daily-cap resetAt, both real UTC
// timestamps) in the viewer's own local time (2026-09-28) -- so an agent doesn't need to know
// Pakistan's UTC offset to read "resumes at 7:00 PM" on their own screen.
const formatResetTime = (ts) => {
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return null;
  }
};

const TextBox = ({
  openDialog,
  setOpenDialog,
  isTeamMemberChat = false,
  droppedFilesPayload = { files: [], stamp: 0 },
  onDroppedFilesConsumed = () => {},
}) => {
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [galleryPopupOpen, setGalleryPopupOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  /** Whole seconds from recorder timer; server may refine via ffprobe. */
  const [recordedAudioDurationSec, setRecordedAudioDurationSec] = useState(null);
  // const [mp3Recorder] = useState(new MicRecorder({ bitRate: 128 }));
  const [message, setMessage] = useState("");
  const currentMessageRef = useRef(""); // Tracks live message value for queue processor restore
  const isSubmittingRef = useRef(false); // Guards against re-entrant handleSendMessage calls (e.g. double Enter press)
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  //template states

  // Message queuing state
  const [isSending, setIsSending] = useState(false);
  const messageQueueRef = useRef([]);
  const processingQueueRef = useRef(false);

  const emojiPickerRef = useRef(null); // Reference to the emoji picker container
  const galleryPopupRef = useRef(null);
  const galleryButtonRef = useRef(null);
  const buttonRef = useRef(null); // Reference to the toggle button
  const fileInputRef = useRef(null); // Reference to the hidden file input
  const textFieldRef = useRef(null); // Reference to the text field for mention handling
  const lastProcessedDropStampRef = useRef(0);

  // Mention handling state
  const [showMentionList, setShowMentionList] = useState(false);
  const [mentionPosition, setMentionPosition] = useState(0);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);

  // Quick reply state
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [quickReplyPosition, setQuickReplyPosition] = useState(0);
  const [filteredQuickReplies, setFilteredQuickReplies] = useState([]);
  const [selectedQuickReplyIndex, setSelectedQuickReplyIndex] = useState(0);
  // Drag-to-reorder (see utils/quickReplyOrder.js) is only offered while
  // browsing the full unfiltered list — once a "/query" narrows it down,
  // dragging a filtered subset around wouldn't map to a sensible position.
  const [isQuickReplyFiltered, setIsQuickReplyFiltered] = useState(false);
  const quickReplyPanelRef = useRef(null); // Reference to the quick-reply panel, for outside-click close
  const quickReplyButtonRef = useRef(null); // Reference to the icon that opens it
  const actionBoxPanelRef = useRef(null); // "Search actions" card, for outside-click close
  const actionBoxButtonRef = useRef(null); // Reference to the Flash icon that opens it
  // CRITICAL: Track which mentions are team members (by name -> team member ID)
  // This allows us to differentiate team member mentions from user mentions even if names collide
  const teamMemberMentionsRef = useRef(new Map()); // Map<mentionName, teamMemberId>

  // AI Assist state (from stagingmerge) — Suggest Reply / Refine / Takeover.
  const [aiAssistSettings, setAiAssistSettings] = useState({ autoSuggestEnabled: false, refineEnabled: false, takeoverEnabled: false });
  const [aiAssistLoading, setAiAssistLoading] = useState(false);
  const aiAssistSettingsFetchedForWorkspaceRef = useRef(null);

  // Rotating "thinking" word + animated "..." loader while the AI is working
  // (Rahim, 2026-08-28: "words changing after a sec or two" + "... should
  // constantly go from ... to . then .. then ... like a loader type") — two
  // independent intervals: the word is slower, the dots cycle continuously.
  const [aiThinkingWordIndex, setAiThinkingWordIndex] = useState(0);
  const [aiThinkingDots, setAiThinkingDots] = useState(".");
  useEffect(() => {
    if (!aiAssistLoading) {
      setAiThinkingWordIndex(0);
      setAiThinkingDots(".");
      return;
    }
    const wordInterval = setInterval(() => {
      setAiThinkingWordIndex((i) => (i + 1) % AI_THINKING_WORDS.length);
    }, 1600);
    const dotFrames = [".", "..", "..."];
    let dotFrame = 0;
    const dotInterval = setInterval(() => {
      dotFrame = (dotFrame + 1) % dotFrames.length;
      setAiThinkingDots(dotFrames[dotFrame]);
    }, 400);
    return () => {
      clearInterval(wordInterval);
      clearInterval(dotInterval);
    };
  }, [aiAssistLoading]);

  const dispatch = useDispatch();
  const messages = useSelector(selectMessages);
  const userDetails = useSelector(selectUserDetailsData);
  const contactInfo = useSelector(selectContactInfo);
  // "Assign to AI" now lives in ChatboxNavbar.jsx (sibling component) — this
  // reads its loading flag so the composer's own "Pondering..." indicator
  // still lights up while that request is in flight, same as before the move.
  const assignToAiLoading = useSelector((state) => state.aiAssistStatus?.assignToAiLoading);
  const selectedTemplate = useSelector(selectSelectedTemplate);
  const teamMembers = useSelector(selectTeamMembersFromInbox);
  const currentTeamMemberChat = useSelector((state) => state.teamMemberChat?.currentChat);
  const rawQuickReplies = useSelector((state) => state.quickReplies?.items || []);
  const quickRepliesWorkspaceId = useSelector(
    (s) => s.auth?.user?.workspaceId || s.workspace?.currentWorkspace?._id
  );
  // Applies the client-only saved drag order on top of the backend's
  // alphabetical list — see utils/quickReplyOrder.js (no backend support yet).
  const quickReplies = useOrderedQuickReplies(rawQuickReplies, quickRepliesWorkspaceId);
  const navigate = useNavigate();
  const { accountid } = useParams();
  const workspaceId = useSelector((s) => s.workspace?.currentWorkspace?._id);
  // Phase 6 ("Client UI") QR gating — Templates/Catalog send via the Meta Cloud API only;
  // WhatsApp blocks both from an unofficial (QR) connection at the protocol level.
  // Per CHAT now: the workspace flag is true only for a QR-ONLY workspace (a workspace with a Meta number as well stays
  // "meta"), so a QR chat in a mixed workspace is recognised by its own platform ("qr").
  const isQrOnlyWorkspace = useSelector((s) => s.workspace?.currentWorkspace?.whatsappConnectionType === "baileys");
  const { connection: chatQrNumber, connections: workspaceQrNumbers } = useChatQrNumber({ threadId: messages?.contactID, isGroup: Boolean(messages?.isGroup) });
  // ...or by its number: a chat whose number is one of the workspace's QR numbers is a QR chat even when its platform is not set
  // on the open chat (the composer must never be left open for a QR number that is down).
  const isQrConnectedWorkspace = isQrOnlyWorkspace || isQrChat(messages) || Boolean(chatQrNumber);
  // The QR rules of this composer (number down, resting, daily cap, no Templates icon) are about a WhatsApp number, so they apply only to chats
  // that go through WhatsApp: QR, Meta WhatsApp, or a chat whose platform is not known yet. A website, Instagram or Facebook chat has no WhatsApp
  // number to wait for; in a QR-only workspace such a chat was being blocked by the workspace's first QR number (2026-10-26).
  const isWhatsAppStyleChat = isWhatsAppStyle(messages?.platform);
  const qrRulesApply = isQrConnectedWorkspace && isWhatsAppStyleChat;
  // Live connection health of the number THIS chat belongs to (a chat is tied to one number from the start, so there is
  // nothing to choose when replying). Kept fresh by InboxAll.jsx's whatsapp_qr:* listeners. Any non-CONNECTED state hides
  // the composer in favor of a reconnect prompt, rather than letting a send fail with an opaque error. Without a known
  // number in a single-number workspace the workspace's first-number fields apply, exactly as before (with several numbers
  // see composerQrState below).
  const whatsappQrState = useSelector((s) => s.workspace?.currentWorkspace?.whatsappQrState);
  // A chat whose own number could not be found, in a workspace with several numbers, is not judged by the workspace's FIRST number (that
  // number says nothing about this chat -- a closed chat or one opened from search used to be blocked by it): see composerQrState, which
  // mirrors the server's rule for a chat without a number (refused only when no number can send).
  const qrNumberUnknown = isChatNumberUnknownAmongSeveral({ chatNumber: chatQrNumber, connections: workspaceQrNumbers });
  const effectiveQrState = composerQrState({ chatNumber: chatQrNumber, connections: workspaceQrNumbers, workspaceState: whatsappQrState });
  const qrNumberName = chatQrNumber?.label || null;
  const qrNumberPhone = chatQrNumber?.phoneNumber || null;
  // Excludes isTeamMemberChat -- an internal team-member thread never goes through WhatsApp at
  // all, so it must stay fully usable regardless of this workspace's QR connection health.
  // Only a number that cannot carry a message at all (logged out, or never paired) replaces the composer. While it is merely reconnecting
  // (or paused after "connection replaced") the composer stays untouched: what is sent is queued and goes out when the number is back.
  const isQrDisconnected = qrRulesApply && !isQrNumberUsableState(effectiveQrState) && !isTeamMemberChat;
  // Staff switched QR off for this account: its numbers were logged out and the QR card is hidden, so the usual "connect it again" prompt
  // would lead nowhere. Existing chats stay readable.
  const qrAccountDisabled = useSelector((st) => st.workspace?.currentWorkspace?.qrPilotEnabled === false);
  // A Meta WhatsApp chat in a workspace whose Meta number is not connected (disconnected on Meta or from our app): sends can only
  // fail, so the composer is replaced by a reconnect prompt, same as a QR number that is down. Only an explicit `false` counts
  // (an unknown value never hides the composer); QR chats, Instagram / Facebook / website chats and team chats are untouched.
  const metaWhatsappConnected = useSelector((s) => s.workspace?.currentWorkspace?.whatsappConnected);
  const isMetaWhatsappChat = !isQrConnectedWorkspace && !isTeamMemberChat && (messages?.platform === "whatsapp" || messages?.platform == null) && !messages?.isGroup;
  // Coexistence ("Reply via"): a Meta contact whose workspace has a QR number linked to its Meta number can be answered through either.
  // The QR number is the default for free text and media; templates, list, catalog and buttons are Meta-only by nature and always
  // go through Meta. Only such chats get the choice -- QR chats, other Meta chats and every other platform are untouched.
  const workspaceQrConnections = useSelector((s) => s.workspace?.currentWorkspace?.whatsappQrConnections);
  // Judged by the chat itself, not by the workspace: with Meta disconnected the workspace turns "QR-only" (isQrOnlyWorkspace), but this is still a
  // Meta contact's chat and the linked QR number must still be offered (and used) for it. The server only sends `linkedQr` for a Meta contact
  // of a workspace that has a number linked to its Meta number, so a plain QR-only workspace is unaffected.
  const isMetaContactChat = isMetaContactThread({ platform: messages?.platform, isGroup: Boolean(messages?.isGroup), isTeamMemberChat, isQrSpecificChat: isQrChat(messages) || Boolean(chatQrNumber) });
  const linkedQrBase = isMetaContactChat && String(contactInfo?._id) === String(messages?.contactID) ? contactInfo?.linkedQr || null : null;
  const linkedQrLive = linkedQrBase && Array.isArray(workspaceQrConnections) ? workspaceQrConnections.find((c) => c.connectionId === linkedQrBase.connectionId) : null;
  const linkedQr = linkedQrBase ? { ...linkedQrBase, ...(linkedQrLive || {}) } : null;
  // "Up" for the Reply-via choice: any state except logged out / never paired (a reconnecting number queues the send, as the server does).
  const linkedQrConnected = isQrNumberUsableState(linkedQr?.state);
  const [replyViaChoice, setReplyViaChoice] = useState(null);
  useEffect(() => { setReplyViaChoice(null); }, [messages?.contactID]);
  // With Meta disconnected only the QR number can send; with the QR number down only Meta can.
  // WhatsApp Web: the contact opened their one-time link, so there is a live browser chat to answer in (docs/WHATSAPP_WEB_HANDOFF_PLAN.md,
  // section 4). Text only; nothing is sent to WhatsApp. Read live (the cached contact can be an hour old), only for a Meta contact.
  const { status: whatsappWebInfo } = useWhatsappWebStatus(messages?.contactID, isMetaContactChat && Boolean(messages?.contactID));
  const whatsappWebChatOpen = Boolean(whatsappWebInfo?.opened);
  const qrReplyChoice = replyViaChoice === "whatsapp_web" ? null : replyViaChoice;
  const qrOrMetaReplyVia = !linkedQr ? (whatsappWebChatOpen ? "meta" : null) : metaWhatsappConnected === false ? "qr" : !linkedQrConnected ? "meta" : (qrReplyChoice || "qr");
  // Until the agent chooses, answer where the contact last wrote from.
  const replyVia = whatsappWebChatOpen && (replyViaChoice === "whatsapp_web" || replyViaChoice == null) ? "whatsapp_web" : qrOrMetaReplyVia;
  const sendViaLinkedQr = replyVia === "qr" && linkedQrConnected;
  const sendViaWhatsappWeb = replyVia === "whatsapp_web";
  const isMetaDisconnected = isMetaWhatsappChat && metaWhatsappConnected === false && !(linkedQr && linkedQrConnected);
  // Send-limit composer-hiding, same treatment as isQrDisconnected above, per number: fed by InboxAll.jsx's
  // whatsapp_qr:burst_pause/burst_resume/daily_limit_alert listeners AND, on load/reload, by workspaceController.js's
  // attachBaileysStatus. The server's own send-time check is the real enforcement; this is the explanation.
  const workspaceBurstPauseUntil = useSelector((s) => s.workspace?.currentWorkspace?.whatsappQrBurstPauseUntil);
  // Rest / daily cap are per number too: with the chat's number unknown among several, the first number's cap must not block the chat (the
  // server still refuses a send that really is over a cap, with its own message).
  const whatsappQrBurstPauseUntil = chatQrNumber ? chatQrNumber.burstPauseUntil : qrNumberUnknown ? null : workspaceBurstPauseUntil;
  const qrBurstDraining = Boolean(chatQrNumber?.burstDraining);
  const workspaceDailyLimitReached = useSelector((s) => s.workspace?.currentWorkspace?.whatsappQrDailyLimitReached);
  const whatsappQrDailyLimitReached = chatQrNumber ? chatQrNumber.dailyLimitReached : qrNumberUnknown ? false : workspaceDailyLimitReached;
  const whatsappQrDailyLimitResetAt = useSelector((s) => s.workspace?.currentWorkspace?.whatsappQrDailyLimitResetAt);
  // One-shot timer so the composer auto-restores the moment the pause window elapses, without
  // needing another live event or a manual refresh -- mirrors this feature's existing
  // auto-recovering states (e.g. STOPPED reconnecting on its own).
  const [, forceBurstPauseRecheck] = useState(0);
  useEffect(() => {
    if (!whatsappQrBurstPauseUntil) return undefined;
    const msLeft = whatsappQrBurstPauseUntil - Date.now();
    if (msLeft <= 0) return undefined;
    const timer = setTimeout(() => forceBurstPauseRecheck((t) => t + 1), msLeft + 250);
    return () => clearTimeout(timer);
  }, [whatsappQrBurstPauseUntil]);
  const isQrBurstPaused = qrRulesApply && !isTeamMemberChat &&
    !!whatsappQrBurstPauseUntil && (qrBurstDraining || Date.now() < whatsappQrBurstPauseUntil);
  const isQrDailyLimitReached = qrRulesApply && !isTeamMemberChat && whatsappQrDailyLimitReached === true;
  const otherMemberId = isTeamMemberChat ? (currentTeamMemberChat?.otherMemberId) : null;
  const activeThreadKey = isTeamMemberChat ? `team-${otherMemberId || ""}` : `contact-${messages?.contactID || ""}`;
  const theme = useTheme(); // Use the theme hook
  // Dark mode: white for the composer's neutral utility icons instead of
  // grey (Rahim, 2026-08-22: "for the dark mood icons we are using grey and
  // some place we are using white use white for all icons"). Light mode
  // unchanged. Send stays the primary-green CTA regardless of mode.
  const composerIconColor = theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.secondary;
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  // Website chat has no 24-hour service window (that's a WhatsApp Cloud API rule) — the
  // server's website send branches skip the check too, so never lock the composer here.
  // A Facebook/Instagram comment doesn't open the standard window (hoursRemaining stays 0),
  // but Meta's Private Reply API still allows exactly one message to that commenter — the
  // backend flags this via hasPendingCommentReply (see getContactById.service.js) so the
  // composer isn't wrongly locked for that one legitimate case.
  // The 24-hour customer-service window is a Meta Business Platform policy only — a
  // QR-connected number is a normal WhatsApp account and never had this restriction, so it
  // must never fall into the template-only ExpiredServiceBox regardless of hoursRemaining. Judged per chat (hasMetaServiceWindow): an Instagram or
  // Facebook chat of a QR-only workspace keeps its 24-hour window like in any other workspace.
  const isDisabled = hasMetaServiceWindow({ platform: messages?.platform, isQrConnectedWorkspace }) && contactInfo?.hoursRemaining === 0 && !contactInfo?.hasPendingCommentReply && !sendViaLinkedQr && !sendViaWhatsappWeb;

  // AI Takeover replaces just the input area (Row 1) with a message + "Take
  // back" button, in the same bordered-card style as the composer itself —
  // the icon toolbar (Row 2) stays visible below regardless (Rahim,
  // 2026-08-28: "render it above the icon the text area" — a nested second
  // border is fine for now, not the final visual pass).
  const aiTakeoverActive = !isTeamMemberChat && contactInfo?.aiTakeover?.enabled;

  // Animated "..." next to the AI Takeover banner's title, continuously while
  // it's active — not tied to aiAssistLoading (that only covers the Suggest/
  // Refine/Takeover REQUESTS; the AI keeps auto-replying in the background
  // long after any of those finish), so it needs its own always-on cycle
  // (Rahim, 2026-08-28: "add ... animation so the user knows ai is working").
  const [aiHandlingDots, setAiHandlingDots] = useState(".");
  useEffect(() => {
    if (!aiTakeoverActive) {
      setAiHandlingDots(".");
      return;
    }
    const dotFrames = [".", "..", "..."];
    let dotFrame = 0;
    const interval = setInterval(() => {
      dotFrame = (dotFrame + 1) % dotFrames.length;
      setAiHandlingDots(dotFrames[dotFrame]);
    }, 400);
    return () => clearInterval(interval);
  }, [aiTakeoverActive]);

  // Whether the floating "AI Smart Reply" badge (top-right of the composer)
  // can render at all — single source of truth shared by the badge's own
  // render condition AND the textarea's reserved paddingRight below, so the
  // two can never drift out of sync (Rahim, 2026-08-28: fixed a bug where
  // typed text wrapped underneath the badge because nothing reserved space
  // for it — the badge is only ~130px wide, so the textarea's first line
  // needs that much clearance on the right). Excluded during AI Takeover —
  // doesn't make sense to suggest a reply while the AI already owns the chat.
  const showSmartReplyBadge = !isTeamMemberChat && aiAssistSettings.autoSuggestEnabled && !recordedAudio && !recording && !aiTakeoverActive;
  const isBlockedContact = !isTeamMemberChat && contactInfo?.isBlocked === true;

  // Coexistence "Reply via" picker, drawn like respond.io's channel picker: a compact trigger at the top-left INSIDE the composer card that opens a
  // small menu with the two channels. Only a linked chat (`linkedQr`) gets it. How a send is routed is decided entirely by replyVia /
  // sendViaLinkedQr above -- this block only draws the choice. It is rendered by the card and, because Official WhatsApp after the 24-hour
  // window swaps the card for ExpiredServiceBox, also above that box, so the agent can always switch back to QR.
  const [replyViaAnchor, setReplyViaAnchor] = useState(null);
  const focusInputAfterReplyViaRef = useRef(false);
  // The picker is unmounted/moved when the chat changes or the composer swaps to ExpiredServiceBox; a kept anchor would reopen the menu on a detached element.
  // A chat also gets the picker while the contact has a WhatsApp Web chat open (a third channel beside QR / Official WhatsApp).
  const hasReplyViaChoices = Boolean(linkedQr) || whatsappWebChatOpen;
  useEffect(() => { setReplyViaAnchor(null); }, [messages?.contactID, hasReplyViaChoices]);
  const replyViaOptions = hasReplyViaChoices
    ? [
        ...(linkedQr ? [{ value: "qr", label: `QR${linkedQr.label ? ` — ${linkedQr.label}` : ""}`, enabled: linkedQrConnected, hint: linkedQrConnected ? "Free text and media" : "This QR number is not connected" }] : []),
        { value: "meta", label: linkedQr ? "Official WhatsApp" : "WhatsApp", enabled: metaWhatsappConnected !== false, hint: metaWhatsappConnected === false ? "Meta is disconnected" : "Templates, list, catalog, buttons and messages inside the 24-hour window" },
        ...(whatsappWebChatOpen ? [{ value: "whatsapp_web", label: "WhatsApp Webchat", enabled: true, hint: "Text and files, in the browser chat the contact opened. Nothing is sent to WhatsApp" }] : []),
      ]
    : [];
  const selectedReplyViaOption = replyViaOptions.find((opt) => opt.value === replyVia) || replyViaOptions[0];
  // Not during AI Takeover: the composer (and its toolbar) is replaced by the takeover message then, so there is nothing to send through.
  const showReplyViaRow = hasReplyViaChoices && !aiTakeoverActive;
  const renderReplyViaPicker = () => {
    if (!hasReplyViaChoices || !selectedReplyViaOption) return null;
    return (
      <>
        <Button
          size="small"
          color="inherit"
          onClick={(e) => {
            focusInputAfterReplyViaRef.current = false;
            setReplyViaAnchor(e.currentTarget);
          }}
          aria-haspopup="menu"
          aria-expanded={Boolean(replyViaAnchor)}
          title={`Reply via — ${selectedReplyViaOption.hint}`}
          startIcon={<WhatsAppIcon sx={{ fontSize: "18px !important", color: "#25D366" }} />}
          endIcon={<ArrowDown2 size={13} color="currentColor" variant="Linear" />}
          sx={{
            textTransform: "none",
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "text.primary",
            minWidth: 0,
            px: 1,
            py: 0.25,
            borderRadius: "8px",
            "& .MuiButton-startIcon": { mr: 0.75, ml: 0 },
            "& .MuiButton-endIcon": { ml: 0.5, mr: 0 },
            "&:hover": { backgroundColor: (t) => t.palette.action.hover },
          }}
        >
          {selectedReplyViaOption.label}
        </Button>
        <Menu
          open={Boolean(replyViaAnchor)}
          anchorEl={replyViaAnchor}
          onClose={() => setReplyViaAnchor(null)}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          // After a choice the agent keeps typing: the focus goes back to the input once the menu has closed (MUI would otherwise return it to the trigger).
          TransitionProps={{
            onExited: () => {
              if (focusInputAfterReplyViaRef.current) {
                focusInputAfterReplyViaRef.current = false;
                textFieldRef.current?.focus();
              }
            },
          }}
          slotProps={{ paper: { sx: { ...DROPDOWN_PAPER_SX, mt: -0.75, minWidth: 280, maxWidth: "calc(100vw - 32px)" } } }}
        >
          {replyViaOptions.map((opt) => (
            <MenuItem
              key={opt.value}
              disabled={!opt.enabled}
              selected={replyVia === opt.value}
              onClick={() => {
                setReplyViaChoice(opt.value);
                focusInputAfterReplyViaRef.current = true;
                setReplyViaAnchor(null);
              }}
              sx={{ ...DROPDOWN_ITEM_SX, mx: 0.5, alignItems: "flex-start", gap: 1.25 }}
            >
              <WhatsAppIcon sx={{ fontSize: 20, color: "#25D366", mt: "1px", flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, lineHeight: 1.3 }}>{opt.label}</Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", lineHeight: 1.3, whiteSpace: "normal" }}>{opt.hint}</Typography>
              </Box>
              {replyVia === opt.value && <TickCircle size={18} variant="Bold" color={theme.palette.primary.main} style={{ flexShrink: 0, marginTop: 1 }} />}
            </MenuItem>
          ))}
        </Menu>
        {metaWhatsappConnected === false && (
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "warning.main", width: "100%", px: 1 }}>
            Meta is disconnected — new customer messages won't arrive until it is reconnected.
          </Typography>
        )}
      </>
    );
  };

  // Auto-focus the composer on every chat switch — same as WhatsApp Web:
  // just start typing right after opening a conversation, no need to click
  // into the box first (Rahim, 2026-08-22). activeThreadKey already covers
  // both contact and team-member chats uniformly. Applies at every screen
  // size, mobile included, per Rahim's explicit call.
  useEffect(() => {
    if (activeThreadKey) {
      textFieldRef.current?.focus();
    }
  }, [activeThreadKey]);

  // Keep currentMessageRef in sync so the queue processor can restore on 403 failure
  useEffect(() => { currentMessageRef.current = message; }, [message]);

  // Load Inbox AI Assist settings once per workspace so the Suggest Reply/Refine/
  // Takeover controls can be hidden entirely when none of them are enabled.
  useEffect(() => {
    if (!workspaceId) return;
    if (aiAssistSettingsFetchedForWorkspaceRef.current === workspaceId) return;
    aiAssistSettingsFetchedForWorkspaceRef.current = workspaceId;
    (async () => {
      try {
        const token = await getAuthToken();
        const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
        const res = await axios.get(
          `${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/inbox-assist/settings`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setAiAssistSettings({
          autoSuggestEnabled: !!res.data?.inboxAssist?.autoSuggestEnabled,
          refineEnabled: !!res.data?.inboxAssist?.refineEnabled,
          takeoverEnabled: !!res.data?.inboxAssist?.takeoverEnabled,
        });
      } catch (err) {
        console.warn("Failed to load Inbox AI Assist settings:", err.message);
      }
    })();
  }, [workspaceId]);

  // Quick replies are fetched lazily, the first time the user types "/" in the textbox
  const quickRepliesFetchedForWorkspaceRef = useRef(null);

  // Process message queue - using useRef to avoid circular dependency
  const processMessageQueueRef = useRef();
  const addToMessageQueueRef = useRef();

  // Update the refs when dispatch changes
  useEffect(() => {
    processMessageQueueRef.current = async () => {
      if (processingQueueRef.current || messageQueueRef.current.length === 0) {
        return;
      }

      processingQueueRef.current = true;
      setIsSending(true);

      while (messageQueueRef.current.length > 0) {
        const messageData = messageQueueRef.current.shift();
        
        try {
          
          if (messageData.via === 'whatsapp_web' && WHATSAPP_WEB_MEDIA_TYPES.includes(messageData.type)) {
            // Into the contact's browser chat: its own endpoint, nothing goes to Meta. The pending bubble is replaced by the
            // server's push (same tempId); a refusal removes it and says why.
            try {
              await sendWhatsappWebMedia({
                workspaceId,
                contactId: messageData.contactId,
                file: messageData[messageData.type],
                tempId: messageData.tempId,
                caption: messageData.caption,
                durationSeconds: messageData.audioDurationSeconds,
              });
            } catch (webError) {
              dispatch(removeOptimisticMessage({ tempId: messageData.tempId }));
              toast.error(webError.message, { id: `whatsapp-web-media-${messageData.tempId}` });
            }
          } else if (messageData.type === 'text') {
            const sendResult = await dispatch(sendMessage(messageData)).unwrap();
            // Lock the composer the instant a Private Reply (comment → DM) send succeeds,
            // rather than waiting for the next contact refetch to notice — see
            // sendMessage.service.js's successData and AwaitingCommentReplyBox.
            if (sendResult?.data?.awaitingCommentReply) {
              dispatch(setContactInfo({ awaitingCommentReply: true, hasPendingCommentReply: false }));
              // The in-memory/IndexedDB contact cache still holds the pre-send snapshot
              // (hasPendingCommentReply: true) — without this, switching away and back
              // within the cache TTL would silently undo the lock above and briefly show
              // ExpiredServiceBox's "Send template" button instead, which doesn't even
              // work for Instagram/Facebook (see AwaitingCommentReplyBox's own comment).
              invalidateCachedContact(messageData.contactId);
            }
          } else if (messageData.type === 'image') {
            await dispatch(sendImageByUpload(messageData)).unwrap();
          } else if (messageData.type === 'video') {
            await dispatch(sendVideoByUpload(messageData)).unwrap();
          } else if (messageData.type === 'document') {
            await dispatch(sendDocumentByUpload(messageData)).unwrap();
          } else if (messageData.type === 'audio') {
            await dispatch(sendAudioByUpload(messageData)).unwrap();
          } else if (messageData.type === 'gallery') {
            await dispatch(sendGalleryItemMessage(messageData)).unwrap();
          } else if (messageData.type === 'catalog') {
            await dispatch(sendCatalogProductsMessage(messageData)).unwrap();
          }
          
          // Delay between queued items: media uploads need more server-side breathing
          // room (upload + WhatsApp API forwarding) than plain text messages.
          if (messageQueueRef.current.length > 0) {
            const isMedia = ['image', 'video', 'audio', 'document'].includes(messageData.type);
            await new Promise(resolve => setTimeout(resolve, isMedia ? 1500 : 300));
          }
          
        } catch (error) {
          console.error(`Failed to send message ${messageData.tempId}:`, error);
          // On 403 (subscription blocked or limit exceeded): remove the optimistic
          // message from the chatbox and restore the text to the input if it's empty.
          if (error.response?.status === 403 && messageData.tempId) {
            dispatch(removeOptimisticMessage({ tempId: messageData.tempId }));
            if (messageData.customMessage && !currentMessageRef.current) {
              setMessage(messageData.customMessage);
            }
          }
          // For other errors: keep existing behaviour (queue continues)
        }
      }

      processingQueueRef.current = false;
      setIsSending(false);
    };

    addToMessageQueueRef.current = (messageData) => {
      messageQueueRef.current.push(messageData);
      processMessageQueueRef.current();
    };
  }, [dispatch]);

  // Called when user selects a template in AllTemplates (WhatsApp only)
  const handleTemplateSelected = (template) => {
    dispatch(updateSelectedTemplate(template));
    setOpenDialog('templateBox'); // Open the TemplateSendBox dialog
  };

  // "Send Full Catalog" (InboxCatalogSend.jsx) resolves to this instead of a product
  // selection — fetches the workspace's wa.me/c/<phoneNumber> deep link (opens the WHOLE
  // connected catalog on WhatsApp, unlike an interactive catalog message's 30-item cap) and
  // drops it into the composer for review, same as picking a template does.
  const handleSendFullCatalogLink = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const token = await getAuthToken();
      const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
      const res = await axios.get(
        `${SERVER_DOMAIN}/api/workspaces/${workspaceId}/catalog-link`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.link) {
        setMessage(res.data.link);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to get catalog link");
    }
  }, [workspaceId]);

  const platform = messages?.platform || "whatsapp";
  const isInstagram = platform === "instagram";
  const isFacebook = platform === "facebook";
  // Website chat (chatbot widget) contacts — see sendChatbotVisitorMessage.service.js's
  // `platform: "website"` — have no WhatsApp Business Catalog/Template equivalent, same
  // reasoning as the existing Instagram/Facebook exclusions below.
  const isWebsite = platform === "website";
  const inboxFileAccept = getInboxAcceptString(platform, isTeamMemberChat);

  const handleOpenTemplateDialog = async () => {
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, ['conversations', 'templateSends']);
    if (blocked) {
      toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 });
      return;
    }
    if (isInstagram || isFacebook) {
      setOpenDialog('instagramTemplate');
    } else {
      setOpenDialog('template');
    }
  };

  const handleInstagramTemplateTypeSelected = (type) => {
    setSelectedInstagramTemplateType(type);
    setOpenDialog('instagramTemplateForm');
  };

  const handleStartRecording = async () => {
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken);
    if (blocked) {
      toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 });
      return;
    }
    setRecording(true);
  };

  const handleOpenClusterDenMedia = async () => {
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, []);
    if (blocked) {
      toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 });
      return;
    }
    setGalleryPopupOpen(false);
    setOpenDialog("clusterdenMedia");
  };

  const handleOpenScheduleDialog = async (hasContent) => {
    if (!hasContent) return;
    if (sendViaWhatsappWeb) {
      toast.error("Scheduled messages go out over WhatsApp, not WhatsApp Webchat. Switch to WhatsApp to schedule.", { id: "whatsapp-web-no-schedule" });
      return;
    }
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, []);
    if (blocked) {
      toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 });
      return;
    }
    setScheduleDialogOpen(true);
  };

  // --- AI Assist (from stagingmerge) ---------------------------------------
  // Suggest Reply — drafts from the last few conversation turns + the workspace's
  // selected Inbox Assist agent. Only ever populates the compose box; the agent still
  // has to review/edit and press Send themselves.
  const handleSuggestReply = useCallback(async () => {
    const contactID = messages?.contactID;
    if (!contactID || !workspaceId || aiAssistLoading) return;
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, []);
    if (blocked) { toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 }); return; }

    const messageAtRequestTime = message;
    const requestStartedAt = Date.now();
    setAiAssistLoading(true);
    try {
      const token = await getAuthToken();
      const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
      const res = await axios.post(
        `${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/inbox-assist/draft`,
        { contactId: contactID },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const draft = res.data?.draft;
      if (!draft) return;
      // Don't clobber text the agent typed while the request was in flight — just let
      // them know the suggestion is available instead of silently overwriting it.
      if (currentMessageRef.current === messageAtRequestTime) {
        setMessage(draft);
      } else {
        toast("Suggested reply is ready — click Suggest Reply again to insert it.");
      }
    } catch (err) {
      console.error("Failed to generate suggested reply:", err);
      toast.error(err.response?.data?.message || "Failed to generate a suggested reply");
    } finally {
      // Keep the thinking indicator visible for a minimum stretch so it reads
      // as a deliberate state, not a flash, when the draft comes back fast
      // (Rahim, 2026-08-28: needed this to actually see the animation to test it).
      const MIN_VISIBLE_MS = 4000;
      const elapsed = Date.now() - requestStartedAt;
      if (elapsed < MIN_VISIBLE_MS) {
        await new Promise((resolve) => setTimeout(resolve, MIN_VISIBLE_MS - elapsed));
      }
      setAiAssistLoading(false);
    }
  }, [messages, workspaceId, message, aiAssistLoading]);

  // Refine — rewrites whatever is already in the compose box. Only enabled when
  // there's text to refine (the toolbar icon itself is disabled until then).
  const handleRefine = useCallback(async () => {
    const messageAtRequestTime = message;
    const text = messageAtRequestTime.trim();
    if (!text || !workspaceId || aiAssistLoading) return;
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, []);
    if (blocked) { toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 }); return; }

    setAiAssistLoading(true);
    try {
      const token = await getAuthToken();
      const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
      const res = await axios.post(
        `${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/inbox-assist/refine`,
        { text },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const refined = res.data?.refined;
      if (!refined) return;
      if (currentMessageRef.current === messageAtRequestTime) {
        setMessage(refined);
      } else {
        toast("Refined text is ready — click Refine again to insert it.");
      }
    } catch (err) {
      console.error("Failed to refine message:", err);
      toast.error(err.response?.data?.message || "Failed to refine the message");
    } finally {
      setAiAssistLoading(false);
    }
  }, [message, workspaceId, aiAssistLoading]);

  // Assign to AI moved out to ChatboxNavbar.jsx as its own top-nav button
  // (Rahim, 2026-08-29: "take out the assign to ai option and take it to
  // the top nav"). handleTakeBackConversation stays here — the composer's
  // AI-active banner (below) remains the only Take Back control.

  // Take Back Conversation — manual hand-back (the other path is aiTakeoverEventHandler.js
  // auto-disabling on the AI's own escalation signal). Also called automatically from
  // the send-message handler when a human sends while AI is active — see there.
  const handleTakeBackConversation = useCallback(async () => {
    const contactID = messages?.contactID;
    if (!contactID || !workspaceId) return;
    try {
      const token = await getAuthToken();
      const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
      await axios.post(
        `${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/inbox-assist/takeover/${contactID}/disable`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Same reasoning as handleAssignToAI — invalidate before refetching so the
      // cache doesn't hand back the still-under-AI snapshot.
      invalidateCachedContact(contactID);
      dispatch(fetchContactById(contactID));
    } catch (err) {
      console.error("Failed to disable AI Takeover:", err);
      toast.error(err.response?.data?.message || "Failed to take back this conversation");
    }
  }, [messages, workspaceId, dispatch]);
  // --- end AI Assist handlers ------------------------------------------------

  const toggleEmojiPicker = async (event) => {
    event.stopPropagation(); // Prevent event propagation
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken, []);
    if (blocked) { toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 }); return; }
    setEmojiPickerOpen((prev) => !prev);
  };
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [selectedAudios, setSelectedAudios] = useState([]);
  const [selectedInstagramTemplateType, setSelectedInstagramTemplateType] = useState(null);
  // Staged catalog products picked from the Catalog button — reviewed as
  // removable rows above the composer (see the "Catalog products selected"
  // preview block below), same "pick now, send on the Send click" flow as
  // selectedImages/selectedDocuments above. null when nothing staged.
  const [selectedCatalogSelection, setSelectedCatalogSelection] = useState(null);

  const getFileKey = useCallback((file) => {
    if (!file) return "";
    return `${file.name || "unknown"}::${file.size || 0}::${file.type || "unknown"}::${file.lastModified || 0}`;
  }, []);

  const ingestFiles = useCallback((files, source = "picker") => {
    const incoming = Array.from(files || []).filter(Boolean);
    if (!incoming.length) return;
    const newImages = [];
    const newVideos = [];
    const newDocuments = [];
    const newAudios = [];
    const errors = [];
    const duplicateNames = [];
    const existingKeys = new Set([
      ...selectedImages.map(getFileKey),
      ...selectedVideos.map(getFileKey),
      ...selectedDocuments.map(getFileKey),
      ...selectedAudios.map(getFileKey),
      ...(recordedAudio ? [getFileKey(recordedAudio)] : []),
    ]);

    incoming.forEach((file) => {
      const fileKey = getFileKey(file);
      if (existingKeys.has(fileKey)) {
        duplicateNames.push(file.name || "Unknown file");
        return;
      }
      const validation = classifyInboxMediaFile(file, platform, isTeamMemberChat);
      if (!validation.valid) {
        errors.push(`${file.name || "File"}: ${validation.error}`);
        return;
      }
      if (validation.kind === "image") {
        newImages.push(file);
      } else if (validation.kind === "video") {
        newVideos.push(file);
      } else if (validation.kind === "audio") {
        newAudios.push(file);
      } else if (validation.kind === "document") {
        newDocuments.push(file);
      }
      existingKeys.add(fileKey);
    });

    if (newImages.length > 0) {
      setSelectedImages((prev) => [...prev, ...newImages]);
    }
    if (newVideos.length > 0) {
      setSelectedVideos((prev) => [...prev, ...newVideos]);
    }
    if (newDocuments.length > 0) {
      setSelectedDocuments((prev) => [...prev, ...newDocuments]);
    }
    if (newAudios.length > 0) {
      setSelectedAudios((prev) => [...prev, ...newAudios]);
    }
    if (errors.length > 0) {
      const firstErrors = errors.slice(0, 3).join("\n");
      const suffix = errors.length > 3 ? `\n...and ${errors.length - 3} more file(s).` : "";
      const actionLabel = source === "paste" ? "Paste" : "Upload";
      toast.error(`${actionLabel} skipped invalid file(s):\n${firstErrors}${suffix}`);
    }
    if (duplicateNames.length > 0) {
      const uniqueDuplicates = Array.from(new Set(duplicateNames));
      const first = uniqueDuplicates.slice(0, 3).join(", ");
      const suffix = uniqueDuplicates.length > 3 ? ` and ${uniqueDuplicates.length - 3} more` : "";
      toast(`${source === "paste" ? "Paste" : "Upload"} skipped duplicate file(s): ${first}${suffix}`);
    }
  }, [
    platform,
    isTeamMemberChat,
    selectedImages,
    selectedVideos,
    selectedDocuments,
    selectedAudios,
    recordedAudio,
    getFileKey,
  ]);

  const handleFileChange = (e) => {
    if (!e.target.files) return;
    ingestFiles(e.target.files, "picker");
    e.target.value = "";
  };

  const handleGalleryItemSend = useCallback(async (galleryItem) => {
    const phoneNumber = messages?.contactNumber;
    const AccountID = userDetails?.AccountID;
    const contactID = messages?.contactID;
    if (!AccountID || (!phoneNumber && !contactID)) return;

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    const mediaTypeMap = { image: 'image', video: 'video', audio: 'audio', document: 'document' };
    const messageType = mediaTypeMap[galleryItem.mediaType] || 'image';
    const originSocketId = getSocketId();

    dispatch(addMessage({
      contactID,
      isForActiveChat: true,
      chat: {
        id: tempId,
        tempId,
        sendername: userDetails?.name || "You",
        senderid: userDetails?._id,
        type: "chat",
        time: new Date().toISOString(),
        status: "pending",
        messageType,
        message: galleryItem.localUrl,
        body: galleryItem.localUrl,
        timestamp: new Date().toISOString(),
        isFromMe: true,
        ...(messageType === "document" ? { documentFileName: galleryItem.originalName } : {}),
      },
    }));

    addToMessageQueueRef.current({
      type: 'gallery',
      galleryItemId: galleryItem._id,
      AccountID,
      to: phoneNumber || undefined,
      contactId: messages?.isGroup ? undefined : (contactID || undefined),
      groupId: messages?.isGroup ? contactID : undefined,
      tempId,
      originSocketId,
    });
  }, [messages, userDetails, dispatch]);

  const handleFilePickerClick = async () => {
    const { blocked, message: blockMsg } = await checkBeforeOutbound(getAuthToken);
    if (blocked) {
      toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 });
      return;
    }
    fileInputRef.current?.click();
  };

  const handleTextBoxPaste = useCallback((event) => {
    const items = Array.from(event.clipboardData?.items || []);
    const files = items
      .filter((item) => item.kind === "file")
      .map((item) => item.getAsFile())
      .filter(Boolean);

    if (!files.length) return;
    event.preventDefault();
    ingestFiles(files, "paste");
  }, [ingestFiles]);

  useEffect(() => {
    if (!droppedFilesPayload?.stamp) return;
    if (lastProcessedDropStampRef.current === droppedFilesPayload.stamp) return;
    if (!Array.isArray(droppedFilesPayload.files) || droppedFilesPayload.files.length === 0) return;
    lastProcessedDropStampRef.current = droppedFilesPayload.stamp;
    ingestFiles(droppedFilesPayload.files, "drop");
    onDroppedFilesConsumed(droppedFilesPayload.stamp);
  }, [droppedFilesPayload, ingestFiles, onDroppedFilesConsumed]);

  // Ensure pending local composer media does not leak between different threads.
  useEffect(() => {
    setSelectedImages([]);
    setSelectedVideos([]);
    setSelectedDocuments([]);
    setSelectedAudios([]);
    setRecordedAudio(null);
    setRecordedAudioDurationSec(null);
    setRecording(false);
  }, [activeThreadKey]);

  const handleDeleteImage = (imageUrl) => {
    setSelectedImages((prevImages) =>
      prevImages.filter((image) => image !== imageUrl)
    );
  };
  const handleDeleteVideo = (video) => {
    // console.log("before: ", selectedVideos);
    setSelectedVideos((prevVideos) =>
      prevVideos.filter((vid) => vid !== video)
    );
    // console.log("after: ", selectedVideos);
  };
  const handleDeleteDocument = (doc) => {
    setSelectedDocuments((prev) => prev.filter((item) => item !== doc));
  };
  const handleDeleteAudio = (audio) => {
    setSelectedAudios((prev) => prev.filter((item) => item !== audio));
  };

  const toggleActionDialog = () => setActionDialogOpen((prev) => !prev);
  // Close emoji picker on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setEmojiPickerOpen(false);
      }
      if (
        galleryPopupRef.current &&
        !galleryPopupRef.current.contains(event.target) &&
        galleryButtonRef.current &&
        !galleryButtonRef.current.contains(event.target)
      ) {
        setGalleryPopupOpen(false);
      }
      if (
        quickReplyPanelRef.current &&
        !quickReplyPanelRef.current.contains(event.target) &&
        quickReplyButtonRef.current &&
        !quickReplyButtonRef.current.contains(event.target)
      ) {
        setShowQuickReplies(false);
        setFilteredQuickReplies([]);
      }
      if (
        actionBoxPanelRef.current &&
        !actionBoxPanelRef.current.contains(event.target) &&
        actionBoxButtonRef.current &&
        !actionBoxButtonRef.current.contains(event.target)
      ) {
        setActionDialogOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle mention selection - MUST be defined before handleKeyDown
  const handleMentionSelect = useCallback((member) => {
    const textBefore = message.substring(0, mentionPosition);
    const textAfter = message.substring(textFieldRef.current?.selectionStart || message.length);
    const mentionText = `@${member.name}`; // Exact mention text as it appears
    const newMessage = `${textBefore}${mentionText} ${textAfter}`;
    setMessage(newMessage);
    setShowMentionList(false);
    setFilteredMembers([]);
    
    // CRITICAL: Track this mention as a team member mention
    // Store the exact mention text (with @) and normalize for matching
    // This allows us to definitively identify team member mentions even if names collide
    const mentionKey = mentionText.toLowerCase().trim(); // "@mohsin ali"
    const teamMemberId = member._id || member.id;
    if (teamMemberId) {
      teamMemberMentionsRef.current.set(mentionKey, {
        teamMemberId,
        teamMemberName: member.name,
        originalMention: mentionText, // Store original for exact matching
        mentionText: mentionText // Store the exact text as it appears
      });
      console.log(`📌 [TEXTBOX] Tracked team member mention:`, {
        mentionKey,
        teamMemberId,
        teamMemberName: member.name,
        mentionText
      });
    }
    
    // Focus back on text field and set cursor position
    setTimeout(() => {
      if (textFieldRef.current) {
        textFieldRef.current.focus();
        const newCursorPos = mentionPosition + member.name.length + 2; // +2 for @ and space
        textFieldRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [message, mentionPosition]);

  const handleQuickReplySelect = useCallback((reply) => {
    // Replace the /query text with the reply message, preserving anything
    // typed after the caret (the icon-triggered panel opens with no query
    // to eat at all, so quickReplyPosition is just the caret position).
    const el = textFieldRef.current;
    const caretPos = el ? (el.selectionStart ?? message.length) : message.length;
    const replaceEnd = Math.max(caretPos, quickReplyPosition);
    const before = message.substring(0, quickReplyPosition);
    const after = message.substring(replaceEnd);
    const newMessage = before + reply.message + after;
    setMessage(newMessage);
    setShowQuickReplies(false);
    setFilteredQuickReplies([]);
    setTimeout(() => {
      if (textFieldRef.current) {
        textFieldRef.current.focus();
        const newCursorPos = before.length + reply.message.length;
        textFieldRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  }, [message, quickReplyPosition]);

  // Icon-triggered entry point for quick replies (same list/state as the
  // "/" trigger — reuses the guarded fetch so opening it never causes an
  // extra DB call beyond the one-per-workspace fetch already in place).
  const handleOpenQuickReplyPanel = useCallback(() => {
    if (
      quickRepliesWorkspaceId &&
      quickRepliesFetchedForWorkspaceRef.current !== quickRepliesWorkspaceId
    ) {
      quickRepliesFetchedForWorkspaceRef.current = quickRepliesWorkspaceId;
      dispatch(fetchQuickReplies(quickRepliesWorkspaceId));
    }
    const caretPos = textFieldRef.current?.selectionStart ?? message.length;
    setQuickReplyPosition(caretPos);
    setFilteredQuickReplies(quickReplies);
    setIsQuickReplyFiltered(false);
    setSelectedQuickReplyIndex(0);
    setShowQuickReplies(true);
    setShowMentionList(false);
    setFilteredMembers([]);
    setTimeout(() => textFieldRef.current?.focus(), 0);
  }, [quickRepliesWorkspaceId, quickReplies, message, dispatch]);

  // Drag-to-reorder inside the popup (see utils/quickReplyOrder.js — client-only,
  // no backend order field exists). `newOrderedIds` covers only the rows the
  // popup actually had visible (the full list when icon-triggered, or the
  // first 10 when opened via "/" with no query yet); anything past that stays
  // in its existing relative order, appended after the reordered visible part.
  const handleQuickReplyReorder = useCallback((newOrderedIds) => {
    if (!quickRepliesWorkspaceId) return;
    const visibleSet = new Set(newOrderedIds);
    const rest = quickReplies.filter((r) => !visibleSet.has(r._id)).map((r) => r._id);
    saveQuickReplyOrder(quickRepliesWorkspaceId, [...newOrderedIds, ...rest]);
    const byId = new Map(quickReplies.map((r) => [r._id, r]));
    setFilteredQuickReplies(newOrderedIds.map((id) => byId.get(id)).filter(Boolean));
  }, [quickReplies, quickRepliesWorkspaceId]);

  // "+" button in the popup header (replaces the old Close/X, per Rahim's ask
  // 2026-08-24) — jumps to Settings > Quick Replies, same navigation pattern
  // already used by Dashboard.jsx's "connect a channel" banner.
  const handleCreateNewQuickReply = useCallback(() => {
    setShowQuickReplies(false);
    setFilteredQuickReplies([]);
    dispatch(setSelectedOption("Quick Replies"));
    navigate(`/home/${accountid}/settings`);
  }, [dispatch, navigate, accountid]);

  const handleSendMessage = useCallback(async () => {
    if (
      !message.trim() &&
      selectedImages.length === 0 &&
      selectedVideos.length === 0 &&
      selectedDocuments.length === 0 &&
      selectedAudios.length === 0 &&
      recordedAudio === null &&
      !selectedCatalogSelection
    ) {
      return;
    }

    if (sendViaWhatsappWeb && selectedCatalogSelection) {
      toast.error("Catalog items can't be sent to a WhatsApp Webchat. Remove them, or reply via WhatsApp.", { id: "whatsapp-web-text-only" });
      return; // nothing is cleared: the agent can switch the channel and send as it is
    }

    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    // A human sending a message is a clear "I'm taking over" signal — don't let AI
    // Takeover keep auto-replying underneath them. Fire-and-forget: never blocks the
    // send itself (handleTakeBackConversation has its own error handling).
    if (!isTeamMemberChat && contactInfo?.aiTakeover?.enabled) {
      handleTakeBackConversation();
    }

    try {
    // Handle team member chat messages
    if (isTeamMemberChat && otherMemberId) {
      // Handle text messages
      if (message.trim()) {
        // Capture the click-time order marker BEFORE the async subscription check below —
        // checkBeforeOutbound does a network call, and concurrent sends (a fast burst) can
        // have their awaits resolve out of click order. Basing tempId/timestamp on a value
        // captured here (not after the await) keeps real-time message order matching the
        // order the user actually hit send in, instead of network-response order.
        const sendClickedAtMs = Date.now();
        // ── subscription pre-check ──
        const { blocked: _tmBlocked, message: _tmBlockMsg } = await checkBeforeOutbound(getAuthToken);
        if (_tmBlocked) {
          toast.error(_tmBlockMsg, { id: 'subscription-blocked', duration: 5000 });
          return; // text stays in textbox
        }
        const tempId = `temp-${sendClickedAtMs}-${Math.random().toString(36).substring(2, 15)}`;
        const messageToSend = message.trim();
        setMessage("");

        try {
          // Optimistic UI update
          const optimisticMessage = {
            id: tempId,
            tempId,
            message: messageToSend,
            messageType: "text",
            timestamp: new Date(sendClickedAtMs).toISOString(),
            status: "sent",
            fromMe: true,
            senderId: userDetails?._id,
            senderName: userDetails?.name || "You",
            receiverId: otherMemberId,
            senderAvatarColor: null // Will be set by backend
          };
          
          dispatch(addTeamMemberMessage({ message: optimisticMessage, otherMemberId }));
          
          // Send message
          await dispatch(sendTeamMemberMessage({
            otherMemberId,
            message: messageToSend,
            messageType: "text",
            tempId,
          })).unwrap();
        } catch (error) {
          console.error("Failed to send team member message:", error);
          // Restore message on error
          setMessage(messageToSend);
        }
      }
      
      // Handle media uploads for team member chats (same pattern as user conversations)
      const handleTeamMemberMediaUpload = async (mediaArray, mediaType) => {
        // Map messageType to upload endpoint mediaType
        const uploadMediaType = mediaType === "image" ? "images" : 
                               mediaType === "video" ? "videos" : 
                               mediaType === "document" ? "documents" : 
                               "audio";
        
        for (const media of mediaArray) {
          const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
          try {
            // Step 1: Upload media file to get URL (same as user conversations)
            const formData = new FormData();
            formData.append("file", media); // mediaUploadMiddleware expects "file" field
            
            const token = await getAuthToken();
            if (!token) {
              console.error("No token found for media upload");
              return;
            }
            
            const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';
            
            // Upload to media endpoint (same endpoint used by user conversations)
            const uploadResponse = await axios.post(
              `${SERVER_DOMAIN}/api/media/upload/${uploadMediaType}`,
              formData,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "multipart/form-data",
                },
              }
            );
            
            const mediaUrl = uploadResponse.data?.data?.url || uploadResponse.data?.url;
            if (!mediaUrl) {
              throw new Error("Failed to get media URL from upload");
            }
            
            // Step 2: Optimistic UI update with local blob URL for immediate display (same as user conversations)
            const isLocalFile = media instanceof File;
            const displayUrl = isLocalFile ? URL.createObjectURL(media) : media;
            
            const optimisticMessage = {
              id: tempId,
              tempId,
              message: displayUrl, // Use blob URL for immediate display
              messageType: mediaType,
              timestamp: new Date().toISOString(),
              status: "pending",
              fromMe: true,
              senderId: userDetails?._id,
              senderName: userDetails?.name || "You",
              receiverId: otherMemberId,
              senderAvatarColor: null,
              ...(mediaType === "document" && isLocalFile && media.name
                ? { documentFileName: media.name }
                : {}),
            };
            
            dispatch(addTeamMemberMessage({ message: optimisticMessage, otherMemberId }));
            
            // Step 3: Send the media URL as a message (same pattern as text messages)
            await dispatch(sendTeamMemberMessage({
              otherMemberId,
              message: mediaUrl, // Send the uploaded media URL
              messageType: mediaType,
              tempId,
              ...(mediaType === "document" && isLocalFile && media.name
                ? { documentFileName: media.name }
                : {}),
            })).unwrap();
            
            // Step 4: Update message with actual URL on success (replace blob URL with actual URL)
            dispatch(addTeamMemberMessage({ 
              message: {
                ...optimisticMessage,
                status: "sent",
                message: mediaUrl, // Replace blob URL with actual URL
              }, 
              otherMemberId 
            }));
          } catch (error) {
            console.error(`Failed to send team member ${mediaType}:`, error);
            // Update message status to failed
            const failedMessage = {
              id: tempId,
              tempId,
              message: media instanceof File ? URL.createObjectURL(media) : media,
              messageType: mediaType,
              timestamp: new Date().toISOString(),
              status: "failed",
              fromMe: true,
              senderId: userDetails?._id,
              senderName: userDetails?.name || "You",
              receiverId: otherMemberId,
              senderAvatarColor: null
            };
            dispatch(addTeamMemberMessage({ 
              message: failedMessage, 
              otherMemberId 
            }));
          }
        }
      };
      
      // Handle images (same pattern as user conversations)
      if (selectedImages.length > 0) {
        await handleTeamMemberMediaUpload(selectedImages, "image");
        setSelectedImages([]);
      }
      
      // Handle videos (same pattern as user conversations)
      if (selectedVideos.length > 0) {
        await handleTeamMemberMediaUpload(selectedVideos, "video");
        setSelectedVideos([]);
      }
      
      // Handle documents (same pattern as user conversations)
      if (selectedDocuments.length > 0) {
        await handleTeamMemberMediaUpload(selectedDocuments, "document");
        setSelectedDocuments([]);
      }
      if (selectedAudios.length > 0) {
        await handleTeamMemberMediaUpload(selectedAudios, "audio");
        setSelectedAudios([]);
      }
      
      // Handle audio (same pattern as user conversations)
      if (recordedAudio) {
        const tempId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
        try {
          // Step 1: Upload audio file
          const formData = new FormData();
          formData.append("file", recordedAudio); // mediaUploadMiddleware expects "file" field
          
          const token = await getAuthToken();
          if (!token) {
            console.error("No token found for audio upload");
            return;
          }
          
          const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';
          
          const uploadResponse = await axios.post(
            `${SERVER_DOMAIN}/api/media/upload/audio`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data",
              },
            }
          );
          
          const mediaUrl = uploadResponse.data?.data?.url || uploadResponse.data?.url;
          if (!mediaUrl) {
            throw new Error("Failed to get audio URL from upload");
          }
          
          // Step 2: Optimistic UI update (same as user conversations)
          const displayUrl = URL.createObjectURL(recordedAudio);
          const optimisticMessage = {
            id: tempId,
            tempId,
            message: displayUrl,
            messageType: "audio",
            timestamp: new Date().toISOString(),
            status: "pending",
            fromMe: true,
            senderId: userDetails?._id,
            senderName: userDetails?.name || "You",
            receiverId: otherMemberId,
            senderAvatarColor: null
          };
          
          dispatch(addTeamMemberMessage({ message: optimisticMessage, otherMemberId }));
          
          // Step 3: Send the audio URL as a message
          await dispatch(sendTeamMemberMessage({
            otherMemberId,
            message: mediaUrl,
            messageType: "audio",
            tempId,
          })).unwrap();
          
          // Step 4: Update message with actual URL on success
          dispatch(addTeamMemberMessage({ 
            message: {
              ...optimisticMessage,
              status: "sent",
              message: mediaUrl
            }, 
            otherMemberId 
          }));
          
          setRecordedAudio(null);
        } catch (error) {
          console.error("Failed to send team member audio:", error);
          // Update message status to failed
          const failedMessage = {
            id: tempId,
            tempId,
            message: URL.createObjectURL(recordedAudio),
            messageType: "audio",
            timestamp: new Date().toISOString(),
            status: "failed",
            fromMe: true,
            senderId: userDetails?._id,
            senderName: userDetails?.name || "You",
            receiverId: otherMemberId,
            senderAvatarColor: null
          };
          dispatch(addTeamMemberMessage({ 
            message: failedMessage, 
            otherMemberId 
          }));
        }
      }
      
      return; // Don't proceed with regular contact chat handling
    }

    const phoneNumber = messages?.contactNumber;
    const AccountID = userDetails?.AccountID;
    const contactID = messages?.contactID;

    if (!AccountID || (!phoneNumber && !contactID)) {
      console.error("Missing required fields: account ID and (phone number or contact ID)");
      return;
    }

    // WhatsApp Web chats: the typed text rides along as the caption of the first image/video/document (audio takes none)
    // instead of going out as a separate message. Over the server's limit it stays a normal text message.
    let whatsappWebCaption =
      sendViaWhatsappWeb &&
      message.trim().length <= WHATSAPP_WEB_MAX_CAPTION_LENGTH &&
      selectedImages.length + selectedVideos.length + selectedDocuments.length > 0
        ? message.trim()
        : "";

    if (message.trim() && !whatsappWebCaption) {
      // Capture the click-time order marker BEFORE the async subscription check below —
      // checkBeforeOutbound does a network call, and concurrent sends (a fast burst) can
      // have their awaits resolve out of click order. Basing tempId/timestamp on a value
      // captured here (not after the await) keeps real-time message order matching the
      // order the user actually hit send in, instead of network-response order.
      const sendClickedAtMs = Date.now();
      // ── subscription pre-check ──
      const { blocked: _blocked, message: _blockMsg } = await checkBeforeOutbound(getAuthToken);
      if (_blocked) {
        toast.error(_blockMsg, { id: 'subscription-blocked', duration: 5000 });
        return; // text stays in textbox, nothing added to Redux
      }

      const tempId = `temp-${sendClickedAtMs}-${Math.random()
        .toString(36)
        .substring(2, 15)}`;
      const messageToSend = message.trim();

      // Clear the text field immediately for instant UI feedback
      setMessage("");

      try {
        // CRITICAL: Detect if this is an internal mention using tracked team member mentions
        // Match against tracked mention text instead of extracting by spaces
        // This ensures we match the full mention name even if there are spaces
        const mentionMatches = [];
        
        // Get all tracked mentions (they have the exact text we need to match)
        const trackedMentions = Array.from(teamMemberMentionsRef.current.entries());
        
        if (trackedMentions.length > 0 && messageToSend.includes('@')) {
          // For each tracked mention, check if it appears in the message
          trackedMentions.forEach(([mentionKey, mentionData]) => {
            // mentionKey is like "@mohsin ali" (lowercase, normalized)
            // mentionData.mentionText is like "@Mohsin Ali" (original case)
            
            // Check if this tracked mention appears in the message (case-insensitive)
            const messageLower = messageToSend.toLowerCase();
            const keyLower = mentionKey.toLowerCase();
            
            // Find all occurrences of this mention in the message
            let searchIndex = 0;
            while ((searchIndex = messageLower.indexOf(keyLower, searchIndex)) !== -1) {
              // Check if it's a valid mention (starts with @ and is followed by space or end)
              const charBefore = searchIndex > 0 ? messageToSend[searchIndex - 1] : '';
              const charAfter = searchIndex + keyLower.length < messageToSend.length 
                ? messageToSend[searchIndex + keyLower.length] 
                : '';
              
              // Valid mention if: starts at @ or after space/newline, and ends at space/end
              const isValidMention = (searchIndex === 0 || /\s/.test(charBefore)) && 
                                    (charAfter === '' || /\s/.test(charAfter));
              
              if (isValidMention) {
                // Extract the actual mention from message (preserve original case)
                
                mentionMatches.push({
                  fullMention: keyLower, // "@mohsin ali"
                  mentionName: mentionData.teamMemberName, // "Mohsin Ali"
                  trackedData: mentionData,
                  position: searchIndex
                });
                
                // Move past this mention to avoid duplicates
                searchIndex += keyLower.length;
              } else {
                searchIndex++;
              }
            }
          });
          
          // Also check for any @ mentions that weren't tracked (user mentions)
          // Find all @ symbols and check if they're already in mentionMatches
          let atIndex = 0;
          while ((atIndex = messageToSend.indexOf('@', atIndex)) !== -1) {
            // Capture the current index so closures below don't reference the
            // mutable loop variable (avoids no-loop-func false positive).
            const currentAtIndex = atIndex;
            // Check if this @ is already covered by a tracked mention
            const isCovered = mentionMatches.some(m =>
              m.position <= currentAtIndex && currentAtIndex < m.position + m.fullMention.length
            );

            if (!isCovered) {
              // This is an untracked mention (user mention) - extract until space
              let endIndex = currentAtIndex + 1;
              while (endIndex < messageToSend.length && messageToSend[endIndex] !== ' ') {
                endIndex++;
              }
              const untrackedMention = messageToSend.substring(currentAtIndex, endIndex).toLowerCase().trim();

              if (untrackedMention.length > 1) { // More than just "@"
                mentionMatches.push({
                  fullMention: untrackedMention,
                  mentionName: untrackedMention.substring(1),
                  trackedData: null, // Not tracked = user mention
                  position: currentAtIndex
                });
              }
            }
            
            atIndex++;
          }
        }
        
        // Check if all mentions are tracked team member mentions
        // CRITICAL: ONLY use tracked mentions for team members (no name matching fallback)
        // User mentions won't be in tracked map, so they'll naturally go to WhatsApp
        let isInternalMention = false;
        let mentionedTeamMemberId = null;
        let mentionsArray = [];
        
        if (mentionMatches.length > 0) {
          // Check if ALL mentions have trackedData (meaning they're team members)
          const allAreTrackedTeamMembers = mentionMatches.every(({ trackedData, fullMention }) => {
            if (trackedData) {
              console.log(`✅ [TEXTBOX MENTION CHECK] Found tracked team member: "${fullMention}" -> ${trackedData.teamMemberName} (ID: ${trackedData.teamMemberId})`);
              // Store the first mentioned team member ID
              if (!mentionedTeamMemberId) {
                mentionedTeamMemberId = trackedData.teamMemberId;
                mentionsArray = [trackedData.teamMemberId];
              }
              return true;
            }
            
            // CRITICAL: If mention has no trackedData, it's a user mention (not a team member)
            // User mentions should go to WhatsApp, so return false
            console.log(`ℹ️ [TEXTBOX MENTION CHECK] Mention "${fullMention}" not tracked - treating as user mention (will send to WhatsApp)`);
            return false;
          });
          
          isInternalMention = allAreTrackedTeamMembers;

          console.log(`🔍 [TEXTBOX MENTION CHECK] Mentions found:`, mentionMatches.map(m => m.fullMention));
          console.log(`🔍 [TEXTBOX MENTION CHECK] All are tracked team members:`, allAreTrackedTeamMembers);
          console.log(`🔍 [TEXTBOX MENTION CHECK] Is internal mention:`, isInternalMention);
        }

        // A team-member "@mention" in a GROUP thread is a team-only note, exactly as in a 1:1 chat: the server saves it in the
        // group's thread and never sends it to WhatsApp. (It used to be force-disabled here, which sent "@name text" into the
        // real WhatsApp group.) A mention that is not a tracked team member still goes out as normal text.

        // CRITICAL: Log message sending for debugging
        console.log(`📤 [TEXTBOX] Sending message:`, {
          tempId,
          contactID,
          phoneNumber,
          messageLength: messageToSend.length,
          messagePreview: messageToSend.substring(0, 50),
          hasMentions: mentionMatches.length > 0,
          isInternalMention,
          mentionMatches: mentionMatches.map(m => m.fullMention),
          senderName: userDetails?.name,
          senderId: userDetails?._id,
          AccountID
        });

        // Track the pending message - pass isInternalMention flag
        trackPendingMessage(tempId, contactID, isInternalMention);
        console.log(`📝 [TEXTBOX] Tracked pending message:`, {
          tempId,
          contactID,
          isInternalMention
        });

        // Get socket ID
        const originSocketId = getSocketId();

        // Immediately add pending message to Redux store for instant UI feedback
        const pendingMessage = {
          contactID,
          isForActiveChat: true,
          chat: {
            id: tempId,
            tempId,
            sendername: userDetails?.name || "You",
            senderid: userDetails?._id,
            type: "chat",
            // Use sendClickedAtMs (captured before checkBeforeOutbound above), not a fresh
            // Date.now() here — see the comment at sendClickedAtMs's declaration.
            time: new Date(sendClickedAtMs).toISOString(),
            // CRITICAL: For internal mentions, set status to "internal" immediately instead of "pending"
            status: isInternalMention ? "internal" : "pending",
            messageType: "text",
            message: messageToSend,
            body: messageToSend,
            timestamp: new Date(sendClickedAtMs).toISOString(),
            isFromMe: true,
            ...(sendViaWhatsappWeb && { from: "whatsapp_web", platform: "whatsapp_web" }),
            // CRITICAL: Set isInternalMention flag for internal mentions
            ...(isInternalMention && { 
              isInternalMention: true,
              // Include mentioned team member ID if found
              ...(mentionedTeamMemberId && {
                mentionedTeamMemberId,
                mentions: mentionsArray
              })
            })
          }
        };
        
        // Clear tracked mentions after using them
        teamMemberMentionsRef.current.clear();
        
        console.log(`💾 [TEXTBOX] Adding pending message to Redux:`, {
          tempId,
          contactID,
          sendername: pendingMessage.chat.sendername,
          senderid: pendingMessage.chat.senderid,
          status: pendingMessage.chat.status,
          isInternalMention
        });
        
        dispatch(addMessage(pendingMessage));

        if (isInternalMention) {
          console.log(`ℹ️ [TEXTBOX] Internal mention detected - backend processes via mention path (queue still receives payload)`);
        addToMessageQueueRef.current({
          type: 'text',
          number: messages?.isGroup ? undefined : (phoneNumber || undefined),
          contactId: messages?.isGroup ? undefined : (contactID || undefined),
          groupId: messages?.isGroup ? contactID : undefined,
          customMessage: messageToSend,
          AccountID,
          tempId,
          originSocketId,
          isInternalMention: true,
          ...(mentionedTeamMemberId && { mentionedTeamMemberId }),
          ...(mentionsArray.length > 0 && { mentions: mentionsArray }),
        });
          console.log(`✅ [TEXTBOX] Internal mention sent to backend for processing:`, {
            tempId,
            contactID,
            isInternalMention
          });
        } else {
          console.log(`📨 [TEXTBOX] Adding message to queue:`, {
            tempId,
            type: 'text',
            number: phoneNumber,
            AccountID,
            originSocketId
          });
          addToMessageQueueRef.current({
            type: 'text',
            number: phoneNumber || undefined,
            contactId: messages?.isGroup ? undefined : (contactID || undefined),
            groupId: messages?.isGroup ? contactID : undefined,
            customMessage: messageToSend,
            AccountID,
            tempId,
            originSocketId,
            isInternalMention: false,
            ...(sendViaLinkedQr ? { via: "qr" } : sendViaWhatsappWeb ? { via: "whatsapp_web" } : {}),
          });
          console.log(`✅ [TEXTBOX] Message queued successfully:`, {
            tempId,
            contactID
          });
        }
      } catch (error) {
        console.error("❌ [TEXTBOX] Failed to queue message:", {
          error: error.message,
          tempId,
          contactID,
          message: messageToSend
        });
        // If there's an immediate error, restore the message
        setMessage(messageToSend);
      }
    }

    // Handle media uploads
    const handleMediaUpload = async (mediaArray, mediaType) => {
      // Capture the click-time order marker BEFORE the async subscription check below —
      // see the sendClickedAtMs comment above for why (checkBeforeOutbound's network call
      // can resolve out of order across concurrent sends). +index below keeps each item in
      // this batch distinct while staying anchored to this batch's click time.
      const batchClickedAtMs = Date.now();
      // ── subscription pre-check (once for the whole batch) ──
      const { blocked: _mBlocked, message: _mBlockMsg } = await checkBeforeOutbound(getAuthToken);
      if (_mBlocked) {
        toast.error(_mBlockMsg, { id: 'subscription-blocked', duration: 5000 });
        return; // files stay selected
      }
      for (const [mediaIndex, media] of mediaArray.entries()) {
        const itemClickedAtMs = batchClickedAtMs + mediaIndex;
        const tempId = `temp-${itemClickedAtMs}-${Math.random()
          .toString(36)
          .substring(2, 15)}`;
        try {
          trackPendingMessage(tempId, contactID);
          const originSocketId = getSocketId();

          // Immediately add pending media message to Redux store
          const isLocalFile = media instanceof File;
          const displayUrl = isLocalFile ? URL.createObjectURL(media) : media;

          const pendingMediaMessage = {
            contactID,
            isForActiveChat: true,
            chat: {
              id: tempId,
              tempId,
              sendername: userDetails?.name || "You",
              senderid: userDetails?._id,
              type: "chat",
              time: new Date(itemClickedAtMs).toISOString(),
              status: "pending",
              messageType: mediaType,
              message: displayUrl,
              body: displayUrl,
              timestamp: new Date(itemClickedAtMs).toISOString(),
              isFromMe: true,
            ...(sendViaWhatsappWeb && { from: "whatsapp_web", platform: "whatsapp_web" }),
              ...(mediaType === "document" && isLocalFile && media.name
                ? { documentFileName: media.name }
                : {}),
            }
          };
          dispatch(addMessage(pendingMediaMessage));

          const caption = whatsappWebCaption;
          if (caption) {
            whatsappWebCaption = "";
            setMessage("");
          }

          // Add media message to queue (contactId for Instagram, groupId for a group thread)
          addToMessageQueueRef.current({
            type: mediaType,
            [mediaType]: media,
            ...(caption ? { caption } : {}),
            AccountID,
            to: phoneNumber || undefined,
            contactId: messages?.isGroup ? undefined : (contactID || undefined),
            groupId: messages?.isGroup ? contactID : undefined,
            tempId,
            originSocketId,
            ...(sendViaLinkedQr ? { via: "qr" } : sendViaWhatsappWeb ? { via: "whatsapp_web" } : {}),
          });
        } catch (error) {
          console.error(`Failed to queue ${mediaType}:`, error);
        }
      }
    };

    // Handle images
    if (selectedImages.length > 0) {
      await handleMediaUpload(selectedImages, "image");
      setSelectedImages([]);
    }


    // Handle videos
    if (selectedVideos.length > 0) {
      await handleMediaUpload(selectedVideos, "video");
      setSelectedVideos([]);
    }

    // Handle documents
    if (selectedDocuments.length > 0) {
      await handleMediaUpload(selectedDocuments, "document");
      setSelectedDocuments([]);
    }
    if (selectedAudios.length > 0) {
      await handleMediaUpload(selectedAudios, "audio");
      setSelectedAudios([]);
    }

    // Handle audio
    if (recordedAudio) {
      // Capture the click-time order marker BEFORE the async subscription check below —
      // see the sendClickedAtMs comment above for why.
      const audioClickedAtMs = Date.now();
      // ── subscription pre-check ──
      const { blocked: _aBlocked, message: _aBlockMsg } = await checkBeforeOutbound(getAuthToken);
      if (_aBlocked) {
        toast.error(_aBlockMsg, { id: 'subscription-blocked', duration: 5000 });
        return; // recorded audio stays
      }
      const tempId = `temp-${audioClickedAtMs}-${Math.random()
        .toString(36)
        .substring(2, 15)}`;
      try {
        trackPendingMessage(tempId, contactID);
        const originSocketId = getSocketId();

        // Immediately add pending audio message to Redux store
        const pendingAudioMessage = {
          contactID,
          isForActiveChat: true,
          chat: {
            id: tempId,
            tempId,
            sendername: userDetails?.name || "You",
            senderid: userDetails?._id,
            type: "chat",
            time: new Date(audioClickedAtMs).toISOString(),
            status: "pending",
            messageType: "audio",
            message: URL.createObjectURL(recordedAudio),
            body: URL.createObjectURL(recordedAudio),
            timestamp: new Date(audioClickedAtMs).toISOString(),
            isFromMe: true,
            ...(sendViaWhatsappWeb && { from: "whatsapp_web", platform: "whatsapp_web" }),
            ...(recordedAudioDurationSec != null && recordedAudioDurationSec > 0
              ? { audioDurationSeconds: recordedAudioDurationSec }
              : {}),
          }
        };
        dispatch(addMessage(pendingAudioMessage));

        // Add audio message to queue (contactId for Instagram, groupId for a group thread)
        addToMessageQueueRef.current({
          type: "audio",
          audio: recordedAudio,
          AccountID,
          to: phoneNumber || undefined,
          contactId: messages?.isGroup ? undefined : (contactID || undefined),
          groupId: messages?.isGroup ? contactID : undefined,
          tempId,
          originSocketId,
          ...(sendViaLinkedQr ? { via: "qr" } : sendViaWhatsappWeb ? { via: "whatsapp_web" } : {}),
          ...(recordedAudioDurationSec != null && recordedAudioDurationSec > 0
            ? { audioDurationSeconds: recordedAudioDurationSec }
            : {}),
        });

        setRecordedAudio(null);
        setRecordedAudioDurationSec(null);
      } catch (error) {
        console.error("Failed to queue audio:", error);
      }
    }

    // Handle staged catalog products (WhatsApp only — see InboxCatalogSend.jsx).
    // Sent as its own message, same as images/documents above rather than
    // merged into the typed text.
    if (selectedCatalogSelection) {
      // Capture the click-time order marker BEFORE the async subscription check below —
      // see the sendClickedAtMs comment above for why.
      const catalogClickedAtMs = Date.now();
      const { blocked: _catBlocked, message: _catBlockMsg } = await checkBeforeOutbound(getAuthToken);
      if (_catBlocked) {
        toast.error(_catBlockMsg, { id: 'subscription-blocked', duration: 5000 });
        return; // selection stays staged
      }

      const tempId = `temp-${catalogClickedAtMs}-${Math.random().toString(36).substring(2, 15)}`;
      const { catalogId, catalogName, products } = selectedCatalogSelection;
      const catalogType = products.length === 1 ? "single" : "multi";
      const productData = { catalogId, catalogName, sectionTitle: catalogName, products };

      try {
        trackPendingMessage(tempId, contactID);
        const originSocketId = getSocketId();

        dispatch(addMessage({
          contactID,
          isForActiveChat: true,
          chat: {
            id: tempId,
            tempId,
            sendername: userDetails?.name || "You",
            senderid: userDetails?._id,
            type: "chat",
            time: new Date(catalogClickedAtMs).toISOString(),
            status: "pending",
            messageType: "catalog",
            message: JSON.stringify({ catalogType, productData }),
            body: JSON.stringify({ catalogType, productData }),
            timestamp: new Date(catalogClickedAtMs).toISOString(),
            isFromMe: true,
          },
        }));

        addToMessageQueueRef.current({
          type: "catalog",
          number: phoneNumber || undefined,
          catalogType,
          productData,
          AccountID,
          tempId,
          originSocketId,
          userId: userDetails?._id,
        });

        setSelectedCatalogSelection(null);
      } catch (error) {
        console.error("Failed to queue catalog message:", error);
      }
    }
    } finally {
      isSubmittingRef.current = false;
    }
  }, [
    message,
    selectedImages,
    selectedVideos,
    selectedDocuments,
    selectedAudios,
    recordedAudio,
    recordedAudioDurationSec,
    selectedCatalogSelection,
    messages,
    userDetails,
    dispatch,
    isTeamMemberChat,
    otherMemberId,
    contactInfo,
    handleTakeBackConversation,
    sendViaLinkedQr,
    sendViaWhatsappWeb,
  ]);

  const handleKeyDown = useCallback(
    (event) => {
      // Handle keyboard navigation for quick reply autocomplete
      if (showQuickReplies && filteredQuickReplies.length > 0) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setSelectedQuickReplyIndex((prev) =>
            prev < filteredQuickReplies.length - 1 ? prev + 1 : prev
          );
          return;
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          setSelectedQuickReplyIndex((prev) => (prev > 0 ? prev - 1 : 0));
          return;
        } else if (event.key === "Enter") {
          event.preventDefault();
          if (filteredQuickReplies[selectedQuickReplyIndex]) {
            handleQuickReplySelect(filteredQuickReplies[selectedQuickReplyIndex]);
          }
          return;
        } else if (event.key === "Escape") {
          event.preventDefault();
          setShowQuickReplies(false);
          setFilteredQuickReplies([]);
          return;
        }
      }

      // Handle keyboard navigation for mention autocomplete
      if (showMentionList && filteredMembers.length > 0) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setSelectedMentionIndex(prev => 
            prev < filteredMembers.length - 1 ? prev + 1 : prev
          );
          return;
        } else if (event.key === "ArrowUp") {
          event.preventDefault();
          setSelectedMentionIndex(prev => prev > 0 ? prev - 1 : 0);
          return;
        } else if (event.key === "Enter") {
          event.preventDefault();
          if (filteredMembers[selectedMentionIndex]) {
            handleMentionSelect(filteredMembers[selectedMentionIndex]);
          }
          return;
        } else if (event.key === "Escape") {
          event.preventDefault();
          setShowMentionList(false);
          setFilteredMembers([]);
          return;
        }
      }
      
      if (event.key === "Enter" && !event.shiftKey && !showMentionList && !showQuickReplies) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage, showMentionList, filteredMembers, selectedMentionIndex, handleMentionSelect, showQuickReplies, filteredQuickReplies, selectedQuickReplyIndex, handleQuickReplySelect]
  );

  const handleMessageChange = useCallback((e) => {
    const value = e.target.value.slice(0, 4096);
    const cursorPos = e.target.selectionStart || 0;
    const textBeforeCursor = value.substring(0, cursorPos);

    // Check for / quick reply trigger
    const lastSlashIndex = textBeforeCursor.lastIndexOf('/');
    if (lastSlashIndex !== -1) {
      const charBeforeSlash = lastSlashIndex > 0 ? textBeforeCursor[lastSlashIndex - 1] : null;
      const isWordBoundary = charBeforeSlash === null || charBeforeSlash === ' ' || charBeforeSlash === '\n';
      if (isWordBoundary) {
        const textAfterSlash = textBeforeCursor.substring(lastSlashIndex + 1);
        if (!textAfterSlash.includes(' ') && !textAfterSlash.includes('\n')) {
          if (
            quickRepliesWorkspaceId &&
            quickRepliesFetchedForWorkspaceRef.current !== quickRepliesWorkspaceId
          ) {
            quickRepliesFetchedForWorkspaceRef.current = quickRepliesWorkspaceId;
            dispatch(fetchQuickReplies(quickRepliesWorkspaceId));
          }
          const query = textAfterSlash.toLowerCase();
          const filtered = query
            ? quickReplies.filter((r) => r.name.toLowerCase().includes(query))
            : quickReplies.slice(0, 10);
          setShowQuickReplies(true);
          setQuickReplyPosition(lastSlashIndex);
          setFilteredQuickReplies(filtered);
          setIsQuickReplyFiltered(Boolean(query));
          setSelectedQuickReplyIndex(0);
          // Also close mention list
          setShowMentionList(false);
          setFilteredMembers([]);
          setMessage(value);
          return;
        }
      }
    }

    setShowQuickReplies(false);
    setFilteredQuickReplies([]);

    // Check for @ mention trigger
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setShowMentionList(true);
        setMentionPosition(lastAtIndex);
        const searchTerm = textAfterAt.toLowerCase().trim();
        if (searchTerm) {
          const filtered = teamMembers.filter(member =>
            member.name && member.name.toLowerCase().includes(searchTerm)
          );
          setFilteredMembers(filtered);
        } else {
          setFilteredMembers(teamMembers.slice(0, 10));
        }
        setSelectedMentionIndex(0);
        setMessage(value);
        return;
      }
    }

    setShowMentionList(false);
    setFilteredMembers([]);
    setMessage(value);
  }, [teamMembers, quickReplies, quickRepliesWorkspaceId, dispatch]);

  // CRITICAL: Clear tracked mentions when message is cleared or changed significantly
  // This prevents stale tracking data from affecting future messages
  useEffect(() => {
    // If message is cleared or very short, clear tracked mentions
    if (!message || message.trim().length < 3) {
      teamMemberMentionsRef.current.clear();
    }
  }, [message]);

  // CRITICAL: Don't render the TextBox if no contact is selected (only for user chats)
  // For team member chats, we should still render TextBox even without messages/contactID
  if (!isTeamMemberChat && (!messages || !messages.contactID || !messages.contactName)) {
    return null;
  }

  // For team member chats, check if we have a valid team member chat
  if (isTeamMemberChat && !otherMemberId) {
    return null;
  }

  // QR (Baileys) connection down (2026-09-26 follow-up), or a send-limit condition currently
  // blocking sends (2026-09-28 follow-up) -- all three replace the entire composer (input, icon
  // row, Send button) with an explanatory banner instead of letting a send attempt reach the
  // backend and fail with an opaque error. Reading existing chat history is unaffected --
  // ChatBody.jsx/ChatBox.jsx mount independently of this component and never check any of these.
  if (isQrDisconnected || isMetaDisconnected || isQrDailyLimitReached || isQrBurstPaused) {
    return (
      <Box
        sx={{
          position: "relative",
          marginX: { xs: 0.5, sm: 0.75, md: 1, lg: 0.75, xl: 0.75 },
          marginBottom: {
            xs: "max(10px, env(safe-area-inset-bottom, 0px))",
            sm: 0,
            md: 0.5,
            lg: 0.5,
            xl: 0.5,
          },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1.25,
          p: 1.5,
          borderRadius: "8px",
          border: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: (theme) => theme.palette.background.paper,
        }}
      >
        <Typography sx={{ fontSize: "0.9375rem", lineHeight: 1.5, color: (theme) => theme.palette.text.secondary }}>
          {isMetaDisconnected ? (
            "Your WhatsApp channel is not connected. Connect it first to send messages. You can still read existing chats."
          ) : isQrDisconnected ? (
            /* Distinct wording for LOGGED_OUT vs every other non-CONNECTED state (2026-09-26
               follow-up): only LOGGED_OUT actually needs a fresh QR scan -- STOPPED/RECONNECTING/
               DISCONNECTED/etc. still have a valid saved session and reconnect on their own
               (restore()'s own fix, same day), so implying "QR required" for those was a false
               alarm, same class of bug just fixed in WhatsAppQrIntegrationStatus.jsx. */
            qrAccountDisabled
              ? "WhatsApp (QR) is turned off for this account, so this number can't send messages. You can still read existing chats. Contact ClusterDen to turn it back on."
              : `Your WhatsApp (QR) number ${[qrNumberName, qrNumberPhone && qrNumberPhone !== qrNumberName ? `+${String(qrNumberPhone).replace(/^\+/, "")}` : null].filter(Boolean).join(" ") || ""} is disconnected. Please connect it first to send messages — a fresh QR scan is required. You can still read existing chats.`
          ) : isQrDailyLimitReached ? (
            `This WhatsApp number has reached its daily send limit — sending will resume automatically${
              formatResetTime(whatsappQrDailyLimitResetAt) ? ` at ${formatResetTime(whatsappQrDailyLimitResetAt)}` : " at the start of the next day"
            }. You can still read existing chats.`
          ) : qrBurstDraining ? (
            `${qrNumberName ? `${qrNumberName} reached` : "This WhatsApp number reached"} its sending limit. Finishing the last messages — then it rests briefly to protect the number. You can still read existing chats.`
          ) : (
            `This WhatsApp number sent a lot of messages recently, so sending is paused briefly to protect it — cooling down${
              formatResetTime(whatsappQrBurstPauseUntil) ? ` until ${formatResetTime(whatsappQrBurstPauseUntil)}` : " for a few minutes"
            }; sending resumes automatically. You can still read existing chats.`
          )}
        </Typography>
        {((isQrDisconnected && !qrAccountDisabled) || isMetaDisconnected) && (
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate(`/home/${accountid}/settings`)}
            sx={{ flexShrink: 0, textTransform: "none" }}
          >
            Connect Workspace
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      marginX={{ xs: 0.5, sm: 0.75, md: 1, lg: 0.75, xl: 0.75 }}
      marginBottom={{
        xs: "max(10px, env(safe-area-inset-bottom, 0px))",
        sm: 0,
        md: 0.5,
        lg: 0.5,
        xl: 0.5,
      }}
    >
      {/* Container Box for images, videos, documents, and staged catalog products */}
      {(selectedImages.length > 0 ||
        selectedVideos.length > 0 ||
        selectedDocuments.length > 0 ||
        selectedAudios.length > 0 ||
        selectedCatalogSelection) && (
        <Box
          sx={{
            // Was position:"absolute" + top:-120 (a magic-number offset guessed to clear
            // the compose box) + width:"100%" — that made this panel always stretch to the
            // full compose-row width no matter how few thumbnails it held (a single 100px
            // thumbnail left most of the bar empty), and a fixed top offset didn't reliably
            // clear the compose box's own height either. bottom:"100%" is the same
            // self-sizing-above-the-compose-box pattern the emoji picker panel right below
            // already uses — always sits flush above regardless of either box's height, and
            // width:"fit-content" makes the panel hug its actual content instead of the full
            // row (Rahim, 2026-09-01: "the container width... not properly aligned"). Staged
            // catalog products are the one exception — their rows are meant to read as part of
            // the message being composed, so that panel should span exactly the same width as
            // the textbox below it, not the full root container (which is wider — the composer
            // card below has its own mx inset from this root). Matching that same mx as left/
            // right insets (with width left as the auto default) lines the two edges up exactly,
            // instead of width:"100%" which spans past the card on both sides.
            position: "absolute",
            bottom: "100%",
            left: selectedCatalogSelection ? { xs: "10px", sm: "12px", md: "14px", lg: "14px", xl: "14px" } : 0,
            right: selectedCatalogSelection ? { xs: "10px", sm: "12px", md: "14px", lg: "14px", xl: "14px" } : "auto",
            mb: 1,
            zIndex: (t) => t.zIndex.modal,
            width: selectedCatalogSelection ? "auto" : "fit-content",
            maxWidth: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 1,
            p: 1.5,
            bgcolor: (theme) => theme.palette.background.paper,
            borderRadius: "8px",
            boxShadow: (theme) =>
              theme.palette.mode === "dark"
                ? "0 2px 4px rgba(0,0,0,0.2)"
                : "0 2px 4px rgba(0,0,0,0.1)",
            border: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          {(selectedImages.length > 0 ||
            selectedVideos.length > 0 ||
            selectedDocuments.length > 0 ||
            selectedAudios.length > 0) && (
          <>
          <Typography
            variant="body2"
            sx={{
              // Was position:"absolute" over the thumbnail row itself (Rahim: "the font
              // size... not properly aligned") — floating text with no reserved space is
              // what let it visually collide with whatever's underneath; a normal in-flow
              // line above the thumbnails can't overlap anything.
              color: (theme) => theme.palette.text.secondary,
              fontWeight: 600,
              fontSize: { xs: "0.75rem", sm: "0.8125rem" },
            }}
          >
            {(() => {
              const fileCount =
                selectedImages.length + selectedVideos.length + selectedDocuments.length + selectedAudios.length + (recordedAudio ? 1 : 0);
              return `${fileCount} file${fileCount === 1 ? "" : "s"} selected`;
            })()}
          </Typography>
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
          {/* Images */}
          {selectedImages.map((image, index) => (
            <Box position="relative" key={index}>
              <img
                src={URL.createObjectURL(image)}
                alt={`Preview ${index}`}
                style={{
                  height: 100,
                  width: 100,
                  objectFit: "cover",
                  borderRadius: "8px",
                }}
              />
              <IconButton
                onClick={() => handleDeleteImage(image)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: (theme) => theme.palette.background.paper,
                  color: (theme) => theme.palette.text.primary,
                  padding: "3px",
                  ":hover": {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}

          {/* Videos */}
          {selectedVideos.map((video, index) => (
            <Box position="relative" key={index}>
              <video
                src={URL.createObjectURL(video)}
                style={{
                  height: 100,
                  width: 100,
                  objectFit: "cover",
                  borderRadius: "8px",
                  pointerEvents: "none", // Disable all interactivity
                }}
                muted
                playsInline
                onLoadedData={(e) => e.target.pause()} // Pause the video immediately to show only the first frame
              />
              <IconButton
                onClick={() => handleDeleteVideo(video)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: (theme) => theme.palette.background.paper,
                  color: (theme) => theme.palette.text.primary,
                  padding: "3px",
                  ":hover": {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}

          {/* Documents */}
          {selectedDocuments.map((document, index) => (
            <Box position="relative" key={index}>
              <Box
                sx={{
                  height: 100,
                  width: 100,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: (theme) => theme.palette.background.default,
                  borderRadius: "8px",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  textAlign: "center",
                  p: 0,
                  overflow: "hidden",
                }}
              >
                <PictureAsPdfIcon
                  fontSize="medium"
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                />
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    maxWidth: "100%",
                    fontSize: "12px",
                    color: (theme) => theme.palette.text.primary,
                  }}
                >
                  {document.name}
                </Typography>
              </Box>
              <IconButton
                onClick={() => handleDeleteDocument(document)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: (theme) => theme.palette.background.paper,
                  color: (theme) => theme.palette.text.primary,
                  padding: "3px",
                  ":hover": {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}
          {/* Uploaded audio files */}
          {selectedAudios.map((audio, index) => (
            <Box position="relative" key={`audio-${index}`}>
              <Box
                sx={{
                  height: 100,
                  width: 140,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: (theme) => theme.palette.background.default,
                  borderRadius: "8px",
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  textAlign: "center",
                  p: 1,
                  overflow: "hidden",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, maxWidth: "100%" }} noWrap>
                  Audio
                </Typography>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ maxWidth: "100%", color: (theme) => theme.palette.text.secondary }}
                >
                  {audio.name}
                </Typography>
              </Box>
              <IconButton
                onClick={() => handleDeleteAudio(audio)}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: (theme) => theme.palette.background.paper,
                  color: (theme) => theme.palette.text.primary,
                  padding: "3px",
                  ":hover": {
                    backgroundColor: (theme) => theme.palette.action.hover,
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          ))}

          {/* Add more — reuses the same picker/appends to the existing selection
              (ingestFiles already does [...prev, ...newFiles], nothing else to wire up)
              (Rahim, 2026-09-01: "give a + sign from which user can add more"). */}
          <IconButton
            onClick={handleFilePickerClick}
            aria-label="Add more files"
            sx={{
              height: 100,
              width: 100,
              borderRadius: "8px",
              border: (theme) => `1px dashed ${theme.palette.divider}`,
              color: (theme) => theme.palette.text.secondary,
              flexShrink: 0,
              "&:hover": {
                borderColor: (theme) => theme.palette.primary.main,
                color: (theme) => theme.palette.primary.main,
                backgroundColor: (theme) => theme.palette.action.hover,
              },
            }}
          >
            <AddCircle size={28} variant="Linear" color="currentColor" />
          </IconButton>
          </Box>
          </>
          )}

          {/* Staged catalog products — removable row list (thumbnail + name/price,
              X to drop one) matching the reference screenshot; a distinct row
              layout from the thumbnail grid above since a product needs its
              name/price legible, not just a square preview. */}
          {selectedCatalogSelection && (
            // Each row is ~48px (36px thumbnail + 0.75 padding) + 0.75 gap — capped at
            // ~5 rows' worth so a large selection scrolls here instead of growing this
            // panel past the compose box.
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, maxHeight: 270, overflowY: "auto" }}>
              {selectedCatalogSelection.products.map((product, index) => {
                const key = product.retailerId || product.id || index;
                const imageUrl = product.imageUrl || product.image_url;
                const hasImage = imageUrl && imageUrl !== "null" && imageUrl !== "undefined" &&
                  (imageUrl.startsWith("http") || imageUrl.startsWith("data:"));
                return (
                  <Box
                    key={key}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 0.75,
                      borderRadius: "8px",
                      backgroundColor: (theme) => theme.palette.action.hover,
                      minWidth: 260,
                    }}
                  >
                    <Box
                      component={hasImage ? "img" : "div"}
                      src={hasImage ? imageUrl : undefined}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "6px",
                        objectFit: "cover",
                        flexShrink: 0,
                        backgroundColor: (theme) => theme.palette.background.default,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontSize: "0.8125rem", fontWeight: 500, color: (theme) => theme.palette.text.primary }}>
                        {product.name || "Unnamed Product"}
                      </Typography>
                      {/* No separate currency suffix — Meta's price already comes back as
                          "amount + currency" together (e.g. "500.00 PKR"); see InboxCatalogSend.jsx. */}
                      <Typography sx={{ fontSize: "0.75rem", color: (theme) => theme.palette.text.secondary }}>
                        {product.price}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      aria-label="Remove product"
                      onClick={() =>
                        setSelectedCatalogSelection((prev) => {
                          if (!prev) return prev;
                          const remaining = prev.products.filter((p) => (p.retailerId || p.id) !== (product.retailerId || product.id));
                          return remaining.length > 0 ? { ...prev, products: remaining } : null;
                        })
                      }
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {emojiPickerOpen && (
        <Box
          ref={emojiPickerRef}
          sx={{
            position: "absolute",
            bottom: "100%",
            mb: 1,
            left: 0,
            right: "auto",
            overflow: "hidden",
            backgroundColor: (t) => t.palette.background.paper,
            borderRadius: "8px",
            boxShadow: (t) =>
              t.palette.mode === "dark"
                ? "0px 4px 10px rgba(0, 0, 0, 0.3)"
                : "0px 4px 10px rgba(0, 0, 0, 0.1)",
            zIndex: (t) => t.zIndex.modal,
            // xs/sm: emoji-mart :host is width:min-content + height:435px — stretching our Box
            // left a full-width empty strip. Cap height to viewport; force host to fill width.
            [theme.breakpoints.down("sm")]: {
              width: "min(100%, calc(100vw - 16px))",
              height:
                "min(360px, calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 168px))",
              maxHeight:
                "min(360px, calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 168px))",
              display: "flex",
              flexDirection: "column",
              "& > div": {
                width: "100%",
                height: "100%",
                minWidth: 0,
                minHeight: 0,
                display: "flex",
                flex: "1 1 auto",
              },
              "& em-emoji-picker": {
                width: "100% !important",
                minWidth: 0,
                height: "100% !important",
                maxHeight: "100% !important",
                boxSizing: "border-box",
              },
            },
          }}
        >
          <Suspense fallback={null}>
            <EmojiPicker
              onEmojiSelect={(emoji) => setMessage((prev) => prev + emoji.native)}
              theme={theme.palette.mode}
              dynamicWidth={isXs}
              previewPosition={isXs ? "none" : "bottom"}
              {...(isXs ? { emojiButtonSize: 30, emojiSize: 20 } : {})}
            />
          </Suspense>
        </Box>
      )}
      {/* Quick Replies / Search Actions / Upload — moved out from being nested
          inside the textfield-only inner Box to sit as direct siblings of the
          emoji picker here, using its exact anchor recipe (position:absolute
          off the OUTERMOST composer Box, bottom:"100%" + mb:1, not a
          calc(100%+8px) nested one level deeper). That inner Box only wrapped
          the textfield, not the icon toolbar row below it, so these 3 popups
          were sitting noticeably closer to the toolbar than the emoji picker
          does (Rahim, 2026-08-24: "see how it has proper padding and gap from
          below and sits above the typing pad" — asked to match all 3 to it). */}
      {showQuickReplies && filteredQuickReplies.length > 0 && (
        // No position styling on this wrapper — QuickReplyAutocomplete.jsx sets
        // its own position:absolute internally (bottom:"100%"+mb:1, matching the
        // emoji picker's recipe below); a positioned wrapper here would create a
        // second containing block and double up the offset.
        <Box ref={quickReplyPanelRef}>
          <QuickReplyAutocomplete
            items={filteredQuickReplies}
            selectedIndex={selectedQuickReplyIndex}
            onSelect={handleQuickReplySelect}
            reorderable={!isQuickReplyFiltered}
            onReorder={handleQuickReplyReorder}
            onCreateNew={handleCreateNewQuickReply}
            onClose={() => {
              setShowQuickReplies(false);
              setFilteredQuickReplies([]);
            }}
          />
        </Box>
      )}
      {actionDialogOpen && (
        <Box
          ref={actionBoxPanelRef}
          sx={{
            position: "absolute",
            bottom: "100%",
            mb: 1,
            left: 0,
            right: "auto",
            width: { xs: "100%", sm: 380 },
            maxWidth: "100%",
            maxHeight: 420,
            overflowY: "auto",
            bgcolor: "background.paper",
            borderRadius: "8px",
            border: (t) => `1px solid ${t.palette.divider}`,
            boxShadow: (t) =>
              t.palette.mode === "dark"
                ? "0px 4px 12px rgba(0,0,0,0.4)"
                : "0px 4px 12px rgba(0,0,0,0.15)",
            zIndex: (t) => t.zIndex.modal,
          }}
        >
          <ChatActionBox
            setOpenDialog={setOpenDialog}
            closeActionBox={toggleActionDialog}
            platform={platform}
            isQrConnectedWorkspace={isQrConnectedWorkspace}
          />
        </Box>
      )}
      {galleryPopupOpen && (
        <Box
          ref={galleryPopupRef}
          sx={{
            position: "absolute",
            bottom: "100%",
            mb: 1,
            left: 0,
            right: "auto",
            width: { xs: "100%", sm: 280 },
            maxWidth: "100%",
            zIndex: (t) => t.zIndex.modal,
            bgcolor: (t) => t.palette.background.paper,
            borderRadius: "8px",
            boxShadow: (t) =>
              t.palette.mode === "dark"
                ? "0px 4px 12px rgba(0,0,0,0.4)"
                : "0px 4px 12px rgba(0,0,0,0.15)",
            border: (t) => `1px solid ${t.palette.divider}`,
            overflow: "hidden",
          }}
        >
          {/* Rows restyled as InboxSettings.jsx's own filter-rail rows (All/You/
              Active) — floating rounded pills inset from the card's edges via
              mx, not flat full-bleed rows with a divider between them. That
              rail's exact hover/gap recipe, not a lookalike (Rahim, 2026-08-24:
              "give up and down padding... take reference from any styling
              done on the inbox page... the hover effect as well"). */}
          {[
            {
              icon: <Monitor size="16" variant="Linear" />,
              label: "Upload from computer",
              onClick: async () => { setGalleryPopupOpen(false); await handleFilePickerClick(); },
            },
            {
              icon: <Cloud size="16" variant="Linear" />,
              label: "Upload from ClusterDen",
              onClick: () => handleOpenClusterDenMedia(),
            },
          ].map(({ icon, label, onClick }, index, arr) => (
            <Box
              key={label}
              onClick={onClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                cursor: "pointer",
                color: "text.primary",
                borderRadius: "8px",
                padding: "8px 10px",
                mx: 1,
                mt: index === 0 ? 1 : 0.5,
                mb: index === arr.length - 1 ? 1 : 0,
                transition: "background-color 0.12s ease",
                "&:hover": {
                  backgroundColor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)"),
                },
              }}
            >
              {icon}
              <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "text.primary" }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      {/* The "Reply via" pills that used to be drawn here, above the composer, now live inside the card as a channel picker (owner decision,
          2026-10-24; see renderReplyViaPicker). The choice and the routing behind it are unchanged. */}
      {isBlockedContact ? (
        <BlockedContactBox blockedByName={contactInfo?.blockedByName} />
      ) : contactInfo?.awaitingCommentReply ? (
        <AwaitingCommentReplyBox contactName={contactInfo?.name} />
      ) : isDisabled ? (
        <>
          {/* Official WhatsApp after the 24-hour window: the card is replaced by this box, so the picker is shown above it to switch back to QR. */}
          {hasReplyViaChoices && (
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mx: { xs: 0.5, sm: 0.75 }, mb: 0.5, px: 0.5 }}>
              {renderReplyViaPicker()}
            </Box>
          )}
          <ExpiredServiceBox />
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            maxWidth: "100%",
            backgroundColor: (t) => t.palette.background.paper,
            border: (t) => `1px solid ${t.palette.divider}`,
            // Same radius as the app's own buttons/badges (Close button,
            // LifecycleStageChip solid variant, Status/Expires-in pills) —
            // not a bespoke value (Rahim, 2026-08-22: "use the same radius
            // as our buttons").
            borderRadius: "8px",
            // AI Smart Reply now floats in the top-right corner instead of
            // taking its own row (Rahim, 2026-08-28), so the card no longer
            // needs the extra room that row required — trimmed back down.
            padding: { xs: "8px", sm: "8px", md: "10px", lg: "10px", xl: "10px" },
            mt: "2px",
            mb: { xs: "12px", sm: "14px", md: "16px", lg: "16px", xl: "16px" },
            mx: { xs: "10px", sm: "12px", md: "14px", lg: "14px", xl: "14px" },
            position: "relative",
          }}
        >
          {/* AI Smart Reply — floats top-right of the composer card, clear of
              the input text (Rahim, 2026-08-28: "top right section of the
              text box"). Plain text/icon, no pill container. */}
          {showSmartReplyBadge && (
            <Box
              onClick={aiAssistLoading ? undefined : handleSuggestReply}
              sx={{
                position: "absolute",
                // Matches the TextField's own inner paddingTop (6px, see below)
                // so this sits on the same line as the "Enter to send..."
                // placeholder text instead of floating above it.
                top: "6px",
                right: "10px",
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                gap: "4px",
                lineHeight: 1.2,
                color: "#7C7BFF",
                // Matches the placeholder's font size (Rahim, 2026-08-28:
                // "increase font size... align it in line with Enter to send").
                fontSize: "0.875rem",
                fontWeight: 500,
                cursor: aiAssistLoading ? "default" : "pointer",
                opacity: aiAssistLoading ? 0.6 : 1,
                backgroundColor: (t) => t.palette.background.paper,
                pt: "10px",
                pr: "10px",
                pl: "6px",
                "&:hover": aiAssistLoading ? {} : { opacity: 0.8 },
              }}
            >
              {aiAssistLoading ? <CircularProgress size={13} sx={{ color: "#7C7BFF" }} /> : <Magicpen size={16} color="#7C7BFF" variant="Linear" />}
              AI Smart Reply
            </Box>
          )}
          {/* Row 1 — the input itself (or the recorder, while recording, or the AI
              Takeover message in place of the input while AI owns the chat), the
              icon toolbar (Row 2, below) stays visible regardless (Rahim,
              2026-08-28: "render it above the icon the text area" — two borders
              visible for now is fine, this isn't the final visual pass). */}
          {/* Coexistence chats only: channel picker (QR / Official WhatsApp) on its own top-left line. The AI Smart Reply badge, which is pinned to the
              card's top-right, then shares this line, so the row keeps clear of it. */}
          {showReplyViaRow && (
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, mb: 0.5, pr: showSmartReplyBadge ? "136px" : 0 }}>
              {renderReplyViaPicker()}
            </Box>
          )}
          {aiTakeoverActive ? (
            // Fade in on mount — the ternary below fully swaps this Box in/out
            // of the tree (not a real cross-fade with the composer it replaces,
            // React unmounts the other branch first), but "appear" still turns
            // the previous instant pop-in into a soft 200ms fade instead of a
            // jarring snap (Rahim, 2026-08-29: "no motion... make the changing
            // of text box better").
            <Fade in appear timeout={200}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                borderRadius: "8px",
                // No border at all (Rahim, 2026-08-28: "remove the border
                // colour for the ai is handleing box"). Grey background, not
                // the accent purple tint (Rahim, 2026-08-28: "change the
                // background text to grey").
                bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#F3F4F6"),
                // More left/right inset so the content sits more centered
                // within the card (Rahim, 2026-08-28).
                px: { xs: "16px", md: "24px" },
                // Taller still (Rahim, 2026-08-28: 14px "not enough" — bumped
                // again, also compensates for Row 2's icon toolbar no longer
                // rendering at all during Takeover, so the composer doesn't
                // visibly shrink when the AI takes over).
                py: "24px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                {/* Real Lottie loading animation, not an icon with a spin
                    bolted on (Rahim, 2026-08-28: "a proper actually made for
                    this sole purpose loader") — Rahim-supplied
                    ai-twinkle-loading.json from LottieFiles. */}
                <Box sx={{ mt: "1px", width: 30, height: 30 }}>
                  <Lottie animationData={aiTwinkleLoadingAnimation} loop autoplay style={{ width: "100%", height: "100%" }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: { xs: "0.9375rem", md: "1rem" }, fontWeight: 600, color: "text.primary" }}>
                    AI is handling this conversation{aiHandlingDots}
                  </Typography>
                  <Typography sx={{ fontSize: { xs: "0.8125rem", md: "0.875rem" }, color: "text.secondary", mt: "2px" }}>
                    Click Take back and the AI agent will stop responding. You'll be assigned to the conversation.
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                onClick={handleTakeBackConversation}
                disabled={aiAssistLoading}
                // Reused verbatim from ChatboxNavbar.jsx's own "Close" button
                // (Rahim, 2026-08-28: pointed at that exact button as the
                // reference — "this is much better") — outlined/neutral,
                // no icon (Rahim, 2026-08-28: "dont use icon for take back").
                // Sized up at md+ (Rahim, 2026-08-28: "for laptop screen take
                // back button is kinda small") + extra right breathing room
                // from the card edge (Rahim, 2026-08-28: "add right spacing").
                sx={{
                  whiteSpace: "nowrap",
                  borderRadius: "8px",
                  backgroundColor: (t) => t.palette.background.default,
                  borderColor: (t) => t.palette.divider,
                  "&:hover": {
                    backgroundColor: (t) => t.palette.action.hover,
                    borderColor: (t) => t.palette.divider,
                  },
                  color: (t) => t.palette.text.primary,
                  textTransform: "none",
                  fontWeight: 500,
                  mr: { xs: "4px", md: "10px" },
                  px: { xs: 1.375, md: 2.25 },
                  py: { xs: 0.625, md: 0.875 },
                  fontSize: { xs: "0.8125rem", md: "0.9375rem" },
                }}
              >
                Take back
              </Button>
            </Box>
            </Fade>
          ) : !recordedAudio && !recording ? (
            <Fade in appear timeout={200}>
            <Box sx={{ position: "relative" }}>
              {(aiAssistLoading || assignToAiLoading) && !message && (
                <Box
                  sx={{
                    position: "absolute",
                    top: "6px",
                    left: "8px",
                    zIndex: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    pointerEvents: "none",
                    color: "#7C7BFF",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    lineHeight: 1.2,
                  }}
                >
                  {AI_THINKING_WORDS[aiThinkingWordIndex]}
                  {aiThinkingDots}
                </Box>
              )}
              <TextField
                inputRef={textFieldRef}
                variant="outlined"
                placeholder={(aiAssistLoading || assignToAiLoading) ? "" : "Enter to send. Shift + Enter to add new line"}
                fullWidth
                size="small"
                multiline
                minRows={2}
                maxRows={4}
                value={message}
                onKeyDown={handleKeyDown}
                onChange={handleMessageChange}
                onPaste={handleTextBoxPaste}
                inputProps={{ maxLength: 4096 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: 0,
                    "& fieldset": { border: "none" },
                  },
                  "& .MuiInputBase-input": {
                    color: (t) => t.palette.text.primary,
                    fontSize: "1rem",
                    // Tighter than before (was 1.4) — the caret's height
                    // follows line-height directly on a textarea, and 1.4
                    // made it visibly taller than the actual glyph height,
                    // reading as "text not centered in the cursor" (Rahim,
                    // 2026-08-22).
                    lineHeight: 1.2,
                    paddingTop: "6px",
                    paddingBottom: "6px",
                    paddingLeft: "8px",
                    // Reserves clearance for the floating AI Smart Reply badge
                    // (top-right, ~130px wide) so typed text can never wrap
                    // underneath it — a plain <textarea> can't flow text around
                    // a floated overlay the way a paragraph can, so the only
                    // reliable fix is reserving the width up front.
                    // Not needed on a coexistence chat: there the badge sits on the channel picker's own line above the input.
                    paddingRight: showSmartReplyBadge && !showReplyViaRow ? "136px" : "8px",
                    "&::placeholder": {
                      color: (t) => t.palette.text.secondary,
                      opacity: 1,
                      fontSize: "0.875rem",
                    },
                  },
                }}
              />
              {showMentionList && filteredMembers.length > 0 && (
                <MentionAutocomplete
                  inputValue={message}
                  cursorPosition={textFieldRef.current?.selectionStart || mentionPosition}
                  onSelect={handleMentionSelect}
                  onClose={() => {
                    setShowMentionList(false);
                    setFilteredMembers([]);
                  }}
                  filteredMembers={filteredMembers}
                  selectedIndex={selectedMentionIndex}
                />
              )}
            </Box>
            </Fade>
          ) : (
            <AudioRecorder
              onRecordingComplete={(file, meta) => {
                setRecordedAudio(file);
                const d = meta?.durationSeconds;
                setRecordedAudioDurationSec(
                  typeof d === "number" && Number.isFinite(d) && d > 0 ? Math.round(d) : null
                );
                setRecording(false);
              }}
              onCancel={() => {
                setRecordedAudio(null);
                setRecordedAudioDurationSec(null);
                setRecording(false);
              }}
              onRecordingReset={() => {
                setRecordedAudio(null);
                setRecordedAudioDurationSec(null);
                setRecording(true);
              }}
            />
          )}

          {/* Row 2 — icon toolbar, utility icons left / send-related icons right,
              same split the reference uses. Every icon here is an existing,
              already-wired feature — just reskinned to Iconsax + neutral color
              instead of the old white-on-dark-green custom SVGs. Not rendered
              at all during AI Takeover (Rahim, 2026-08-28: "remove them"). */}
          {!aiTakeoverActive && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
              {!recordedAudio && !recording && (
                <>
                  {/* Inbox AI Assist (from stagingmerge) — hidden entirely unless the
                      workspace has Refine or AI Takeover turned on. Suggest Reply moved
                      out to its own standalone pill above the input (Rahim, 2026-08-27:
                      "suggest reply should be outside"), not buried in this menu anymore.
                      Moved to the front of the toolbar with a divider after it (Rahim,
                      2026-08-28: "like they have done for whatsapp we will do for ai" —
                      WhatsApp puts its own icon left of everything else, divider, then
                      the rest of the icons). Unstyled MUI icons on purpose — "crappy ui"
                      per Rahim, redesign comes later once every endpoint is confirmed
                      working. */}
                  {/* Refine — direct one-click trigger now, no dropdown (Rahim,
                      2026-08-29: "user can just click the icon and the text
                      refine starts"). Was a menu-opener button holding a
                      single "Refine" item; collapsed to match Suggest Reply's
                      own one-click pattern above now that Assign to AI moved
                      out to ChatboxNavbar.jsx and it's the only action left.
                      AiRefineIcon (pen + sparkle "ai-editing" glyph, sourced
                      from Hugeicons' free set — Rahim, 2026-08-29, replacing
                      an earlier document+sparkle pick) instead of the old
                      MagicStar/Edit2 attempts; grey/disabled with no text
                      typed, purple once there's a draft to refine. The old
                      contactInfo?.aiTakeover SmartToyIcon swap was actually
                      unreachable — this whole Row 2 toolbar doesn't render at
                      all while AI Takeover is active (see the !aiTakeoverActive
                      guard around this row). */}
                  {!isTeamMemberChat && aiAssistSettings.refineEnabled && (
                    <>
                      <IconButtonWithTooltip
                        // Explains the disabled state instead of leaving it a
                        // mystery — same "Type a message first" copy the old
                        // dropdown-item subtitle used before Refine collapsed
                        // to a direct icon (Rahim, 2026-08-29: "add a note
                        // cause its disabled").
                        label={
                          message.trim() ? (
                            "Refine"
                          ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                              <span>Refine</span>
                              {/* opacity (not a separate grey token) so it reads
                                  as muted against the tooltip bubble's own
                                  color regardless of light/dark mode — the
                                  bubble itself flips dark/light, a fixed grey
                                  would only read correctly in one of the two. */}
                              <span style={{ opacity: 0.7 }}>Type a message first</span>
                            </Box>
                          )
                        }
                        onClick={handleRefine}
                        disabled={aiAssistLoading || !message.trim()}
                        sx={composerIconBtnSx}
                      >
                        {aiAssistLoading ? (
                          <CircularProgress size={16} sx={{ color: "#7C7BFF" }} />
                        ) : (
                          // Was reusing composerIconColor for the empty state —
                          // the exact same color every other active toolbar icon
                          // uses, so the button never actually looked disabled
                          // even though clicks were already blocked (Rahim,
                          // 2026-08-29: "right now it looks as the others").
                          // theme.palette.action.disabled + reduced opacity is
                          // MUI's own dedicated disabled-content treatment.
                          <AiRefineIcon
                            width={22}
                            height={22}
                            strokeWidth={message.trim() ? 1.9 : 1.5}
                            style={{
                              color: message.trim() ? "#7C7BFF" : theme.palette.action.disabled,
                              opacity: message.trim() ? 1 : 0.75,
                            }}
                          />
                        )}
                      </IconButtonWithTooltip>
                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: "6px" }} />
                    </>
                  )}
                  <IconButtonWithTooltip label="Emoji" ref={buttonRef} onClick={toggleEmojiPicker} sx={composerIconBtnSx}>
                    <EmojiHappy size={20} color={composerIconColor} variant="Linear" />
                  </IconButtonWithTooltip>
                  <IconButtonWithTooltip
                    label="Attach"
                    ref={galleryButtonRef}
                    onClick={() => setGalleryPopupOpen((prev) => !prev)}
                    sx={composerIconBtnSx}
                  >
                    <Paperclip2 size={19} color={composerIconColor} variant="Linear" />
                  </IconButtonWithTooltip>
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept={inboxFileAccept}
                    onChange={handleFileChange}
                  />
                  {!isTeamMemberChat && !isWebsite && (
                    <>
                      {/* Fully hidden (not just disabled), 2026-09-26 follow-up — a QR-connected
                          number can never send templates (WhatsApp blocks it at the protocol
                          level), so the icon no longer appears at all rather than showing greyed
                          out with an explanatory tooltip. Dialogs below stay mounted unconditionally
                          (harmless, gated on openDialog, which nothing can set to 'template'/
                          'templateBox' once this button and ChatActionBox.jsx's own duplicate
                          entry point are both hidden). */}
                      {!qrRulesApply && (
                        <IconButtonWithTooltip
                          label="Templates"
                          onClick={handleOpenTemplateDialog}
                          sx={composerIconBtnSx}
                        >
                          <DocumentText1 size={19} color={composerIconColor} variant="Linear" />
                        </IconButtonWithTooltip>
                      )}
                      <TemplateSend
                        open={openDialog === 'template'}
                        onClose={() => setOpenDialog(null)}
                        onTemplateSelected={handleTemplateSelected}
                      />
                      <TemplateSendBox
                        open={openDialog === 'templateBox'}
                        onClose={() => setOpenDialog(null)}
                        onBack={() => {
                          dispatch(updateSelectedTemplate(null));
                          setOpenDialog("template");
                        }}
                        selectedTemplate={selectedTemplate}
                      />
                      <InstagramTemplateSend
                        open={openDialog === 'instagramTemplate'}
                        onClose={() => { setOpenDialog(null); setSelectedInstagramTemplateType(null); }}
                        onSelectType={handleInstagramTemplateTypeSelected}
                        platform={platform}
                      />
                      <InstagramTemplateForm
                        open={openDialog === 'instagramTemplateForm'}
                        onClose={() => { setOpenDialog(null); setSelectedInstagramTemplateType(null); }}
                        onBack={() => {
                          setSelectedInstagramTemplateType(null);
                          setOpenDialog('instagramTemplate');
                        }}
                        templateType={selectedInstagramTemplateType}
                        contactID={messages?.contactID}
                        platform={platform}
                      />
                    </>
                  )}
                  {/* Catalog — WhatsApp only, matching /api/messages/send-catalog's own
                      Cloud API scope (Instagram/Facebook/website have no equivalent
                      interactive product message). */}
                  {!isTeamMemberChat && !isInstagram && !isFacebook && !isWebsite && (
                    <>
                      {/* Fully hidden (not just disabled), 2026-09-26 follow-up — same reasoning
                          as Templates above. */}
                      {!isQrConnectedWorkspace && (
                        <IconButtonWithTooltip
                          label="Catalog"
                          onClick={() => setOpenDialog('catalogSend')}
                          sx={composerIconBtnSx}
                        >
                          <ShoppingCart size={19} color={composerIconColor} variant="Linear" />
                        </IconButtonWithTooltip>
                      )}
                      <InboxCatalogSend
                        open={openDialog === 'catalogSend'}
                        onClose={() => setOpenDialog(null)}
                        onProductsSelected={(selection) => {
                          setOpenDialog(null);
                          if (selection?.fullCatalogLink) {
                            handleSendFullCatalogLink();
                            return;
                          }
                          setSelectedCatalogSelection(selection);
                        }}
                        platform={platform}
                      />
                    </>
                  )}
                  {!isTeamMemberChat && (
                    <>
                      {/* Hidden for a group thread (2026-09-26 follow-up) -- every action this
                          menu offers (Close conversation, Assign, Add label) is 1:1-only, none
                          of it applies to a group (decision #2/#4); normal QR/Meta 1:1 chats keep
                          showing it unchanged. */}
                      {!messages?.isGroup && (
                        <IconButtonWithTooltip label="Actions" ref={actionBoxButtonRef} onClick={toggleActionDialog} sx={composerIconBtnSx}>
                          <Flash size={19} color={composerIconColor} variant="Linear" />
                        </IconButtonWithTooltip>
                      )}
                      <IconButtonWithTooltip label="Quick Reply" ref={quickReplyButtonRef} onClick={handleOpenQuickReplyPanel} sx={composerIconBtnSx}>
                        <MessageFavorite size={19} color={composerIconColor} variant="Linear" />
                      </IconButtonWithTooltip>
                    </>
                  )}
                </>
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
              {(message.trim() ||
                selectedImages.length > 0 ||
                selectedVideos.length > 0 ||
                selectedDocuments.length > 0 ||
                selectedAudios.length > 0 ||
                recordedAudio ||
                selectedCatalogSelection) && (
                <>
                  {/* Website chat has no scheduled sends — template/catalog aren't sent
                      for it either (see isWebsite above). */}
                  {/* Same for WhatsApp Web: a scheduled send goes out over WhatsApp at its time, never into the
                      browser chat, so it is not offered while that channel is selected. */}
                  {!isWebsite && !sendViaWhatsappWeb && (
                  <IconButtonWithTooltip
                    label="Schedule"
                    onClick={() => {
                      const hasContent = message.trim() || selectedImages.length > 0 ||
                        selectedVideos.length > 0 || selectedDocuments.length > 0 || selectedAudios.length > 0 || recordedAudio || selectedCatalogSelection;
                      handleOpenScheduleDialog(hasContent);
                    }}
                    disabled={
                      isSending ||
                      isDisabled ||
                      (!recordedAudio &&
                      !message.trim() &&
                      !selectedImages.length > 0 &&
                      !selectedVideos.length > 0 &&
                      !selectedDocuments.length > 0 &&
                      !selectedAudios.length > 0 &&
                      !selectedCatalogSelection)
                    }
                    sx={{ ...composerIconBtnSx, opacity: isSending || isDisabled ? 0.5 : 1 }}
                  >
                    <Clock size={19} color={composerIconColor} variant="Linear" />
                  </IconButtonWithTooltip>
                  )}
                  <IconButtonWithTooltip
                    label="Send"
                    onClick={handleSendMessage}
                    disabled={
                      isSending ||
                      isDisabled ||
                      (!recordedAudio &&
                      !message.trim() &&
                      !selectedImages.length > 0 &&
                      !selectedVideos.length > 0 &&
                      !selectedDocuments.length > 0 &&
                      !selectedAudios.length > 0 &&
                      !selectedCatalogSelection)
                    }
                    sx={{ ...composerIconBtnSx, color: (t) => t.palette.primary.main, opacity: isSending || isDisabled ? 0.5 : 1 }}
                  >
                    <Send2 size={22} color={theme.palette.primary.main} variant="Bold" />
                  </IconButtonWithTooltip>
                </>
              )}

              {!recordedAudio &&
                !recording &&
                !message.trim() &&
                !selectedImages.length > 0 &&
                !selectedVideos.length > 0 &&
                !selectedDocuments.length > 0 &&
                !selectedAudios.length > 0 &&
                !selectedCatalogSelection && (
                  <IconButtonWithTooltip
                    label="Voice Message"
                    onClick={handleStartRecording}
                    disabled={isSending}
                    sx={{ ...composerIconBtnSx, opacity: isSending ? 0.5 : 1 }}
                  >
                    <Microphone2 size={19} color={composerIconColor} variant="Linear" />
                  </IconButtonWithTooltip>
                )}
            </Box>
          </Box>
          )}
        </Box>
      )}

      {/* ClusterDen Media Dialog */}
      <ClusterDenMediaDialog
        open={openDialog === "clusterdenMedia"}
        onClose={() => setOpenDialog(null)}
        onFilesSelected={(files) => ingestFiles(files, "picker")}
        onGalleryItemSelected={handleGalleryItemSend}
        platform={platform}
        isTeamMemberChat={isTeamMemberChat}
      />

      {/* Schedule Message Dialog */}
      <ScheduleMessageDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        onMessageScheduled={() => {
          // Clear all message content after successful scheduling
          setMessage("");
          setSelectedImages([]);
          setSelectedVideos([]);
          setSelectedDocuments([]);
          setSelectedAudios([]);
          setRecordedAudio(null);
          setScheduleDialogOpen(false);
        }}
        messageType={
          recordedAudio ? "audio" :
          selectedAudios.length > 0 ? "audio" :
          selectedImages.length > 0 ? "image" :
          selectedVideos.length > 0 ? "video" :
          selectedDocuments.length > 0 ? "document" :
          "text"
        }
        messageContent={message.trim()}
        mediaFile={
          recordedAudio || 
          (selectedAudios.length > 0 ? selectedAudios[0] : null) ||
          (selectedImages.length > 0 ? selectedImages[0] : null) ||
          (selectedVideos.length > 0 ? selectedVideos[0] : null) ||
          (selectedDocuments.length > 0 ? selectedDocuments[0] : null)
        }
        mediaFileName={
          recordedAudio?.name ||
          (selectedAudios.length > 0 ? selectedAudios[0]?.name : null) ||
          (selectedImages.length > 0 ? selectedImages[0]?.name : null) ||
          (selectedVideos.length > 0 ? selectedVideos[0]?.name : null) ||
          (selectedDocuments.length > 0 ? selectedDocuments[0]?.name : null)
        }
        contactId={messages?.contactID}
        AccountID={userDetails?.AccountID}
      />
    </Box>
  );
};

export default TextBox;
